import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Settings, Clock, Save, Loader2, Mail, Bell, FileText, Users, Download, TestTube, Building2, Database, AlertTriangle, CheckCircle2, Upload, FolderDown, FolderUp, File, Trash2, FormInput, History, FolderOpen, Briefcase, Archive, RefreshCw, Play, Server, Globe, ArrowRight, ArrowLeft, Cable, Copy, CheckCircle, Plus, Search } from "lucide-react";
import { Input } from "../../components/ui/input";

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [savingReminders, setSavingReminders] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [savingTeamLeaderReport, setSavingTeamLeaderReport] = useState(false);
  
  // Reminder time settings
  const [todayReminderTime, setTodayReminderTime] = useState("18:00");
  const [finalReminderTime, setFinalReminderTime] = useState("10:00");
  const [todayReminderEnabled, setTodayReminderEnabled] = useState(true);
  const [finalReminderEnabled, setFinalReminderEnabled] = useState(true);
  
  // Email notification settings
  const [ccRemindersToTeamLeader, setCcRemindersToTeamLeader] = useState(false);
  const [ccRemindersToAdmin, setCcRemindersToAdmin] = useState(false);
  const [ccRemindersToTaj, setCcRemindersToTaj] = useState(false);
  const [notifyTeamLeaderOnSubmission, setNotifyTeamLeaderOnSubmission] = useState(false);
  const [notifyAdminOnSubmission, setNotifyAdminOnSubmission] = useState(false);
  const [notifyTajOnSubmission, setNotifyTajOnSubmission] = useState(false);
  const [productivityThreshold, setProductivityThreshold] = useState("");
  const [productivityAlertEmail, setProductivityAlertEmail] = useState("taj@jobtalk.ai");
  
  // Team Leader Report settings
  const [teamLeaderReportEnabled, setTeamLeaderReportEnabled] = useState(false);
  const [teamLeaderReportTime, setTeamLeaderReportTime] = useState("09:00");
  const [includeTasksToday, setIncludeTasksToday] = useState(true);
  const [includeChallenges, setIncludeChallenges] = useState(true);
  const [includePlanTomorrow, setIncludePlanTomorrow] = useState(true);
  const [includeAiDiscovery, setIncludeAiDiscovery] = useState(true);
  
  // Test Report Dialog
  const [testReportDialogOpen, setTestReportDialogOpen] = useState(false);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [selectedTeamLeaders, setSelectedTeamLeaders] = useState([]);
  const [loadingTeamLeaders, setLoadingTeamLeaders] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [sendReportToLeaders, setSendReportToLeaders] = useState(false);
  
  // Export/Import
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedImportFiles, setSelectedImportFiles] = useState({});
  const [importResult, setImportResult] = useState(null);
  const [combinedBackupFile, setCombinedBackupFile] = useState(null);
  const [selectedFolderName, setSelectedFolderName] = useState("");
  const [folderFiles, setFolderFiles] = useState([]);
  const combinedFileInputRef = useRef(null);
  
  // CRM Export/Import
  const [crmExportDialogOpen, setCrmExportDialogOpen] = useState(false);
  const [crmImportDialogOpen, setCrmImportDialogOpen] = useState(false);
  const [crmExporting, setCrmExporting] = useState(false);
  const [crmImporting, setCrmImporting] = useState(false);
  const [crmBackupFile, setCrmBackupFile] = useState(null);
  const [crmImportResult, setCrmImportResult] = useState(null);
  const crmFileInputRef = useRef(null);
  
  // New Unified Export/Import ALL
  const [exportingAll, setExportingAll] = useState(false);
  const [exportingEnv, setExportingEnv] = useState(false);
  const [importAllDialogOpen, setImportAllDialogOpen] = useState(false);
  const [importingAll, setImportingAll] = useState(false);
  const [importAllFile, setImportAllFile] = useState(null);
  const [importAllResult, setImportAllResult] = useState(null);
  const [showImportWarning, setShowImportWarning] = useState(false);
  const [exportResultDialogOpen, setExportResultDialogOpen] = useState(false);
  const [exportResultData, setExportResultData] = useState(null);

  // Forms Export/Import
  const [exportingForms, setExportingForms] = useState(false);
  const [importFormsDialogOpen, setImportFormsDialogOpen] = useState(false);
  const [importingForms, setImportingForms] = useState(false);
  const [importFormsFile, setImportFormsFile] = useState(null);
  const [importFormsResult, setImportFormsResult] = useState(null);

  
  // Database Migration
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);
  const [exportingMigration, setExportingMigration] = useState(false);
  const [migrationStep, setMigrationStep] = useState(1); // 1: Choose, 2: Export/Import
  const [migrationMode, setMigrationMode] = useState(null); // 'export' or 'import'
  const [newDbConnectionString, setNewDbConnectionString] = useState('');
  const [newDbName, setNewDbName] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState(null);
  const [migrationFile, setMigrationFile] = useState(null);
  const [migratingData, setMigratingData] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const migrationFileInputRef = useRef(null);
  
  // Automatic Backup Settings
  const [backupSettings, setBackupSettings] = useState({
    enabled: false,
    frequency: 'daily',
    retention_days: 13,
    backup_time: '02:00'
  });
  const [backupSettingsLoading, setBackupSettingsLoading] = useState(true);
  const [savingBackupSettings, setSavingBackupSettings] = useState(false);
  const [backupList, setBackupList] = useState([]);
  const [backupListLoading, setBackupListLoading] = useState(false);
  const [runningBackup, setRunningBackup] = useState(false);

  // Bulk Delete state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deleteConditions, setDeleteConditions] = useState([
    { field: 'work_email', operator: 'is_null', value: '' }
  ]);
  const [deleteCompaniesWithNoLeads, setDeleteCompaniesWithNoLeads] = useState(false);
  const [deletingLeads, setDeletingLeads] = useState(false);
  const [deletePreviewCount, setDeletePreviewCount] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Smart Bulk Delete state
  const [smartDeletePreview, setSmartDeletePreview] = useState(null);
  const [loadingSmartPreview, setLoadingSmartPreview] = useState(false);
  const [executingSmartDelete, setExecutingSmartDelete] = useState(false);
  
  // Selective Export/Import state
  const [selectiveExportDialogOpen, setSelectiveExportDialogOpen] = useState(false);
  const [selectiveImportDialogOpen, setSelectiveImportDialogOpen] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [exportingSelected, setExportingSelected] = useState(false);
  const [importingSelected, setImportingSelected] = useState(false);
  const [selectiveImportFile, setSelectiveImportFile] = useState(null);
  const [selectiveImportResult, setSelectiveImportResult] = useState(null);
  const selectiveImportFileRef = useRef(null);
  
  // Sync Database state
  const [syncDatabaseDialogOpen, setSyncDatabaseDialogOpen] = useState(false);
  const [syncMode, setSyncMode] = useState('merge'); // 'merge' or 'override'
  const [syncConnectionString, setSyncConnectionString] = useState('');
  const [syncDatabaseName, setSyncDatabaseName] = useState('');
  const [testingSyncConnection, setTestingSyncConnection] = useState(false);
  const [syncConnectionTestResult, setSyncConnectionTestResult] = useState(null);
  const [syncingDatabase, setSyncingDatabase] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  
  // Environment Variables
  const [envVarsDialogOpen, setEnvVarsDialogOpen] = useState(false);
  const [envVars, setEnvVars] = useState({ backend: {}, frontend: {} });
  const [loadingEnvVars, setLoadingEnvVars] = useState(false);
  const [savingEnvVars, setSavingEnvVars] = useState(false);
  const [activeEnvTab, setActiveEnvTab] = useState('backend');
  const [editedEnvVars, setEditedEnvVars] = useState({ backend: {}, frontend: {} });
  
  const fileInputRefs = {
    users: useRef(null),
    daily_logs: useRef(null),
    teams: useRef(null),
    forms: useRef(null),
    invites: useRef(null),
    email_logs: useRef(null),
    settings: useRef(null)
  };

  useEffect(() => {
    fetchSettings();
    fetchBackupSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [remindersRes, notificationsRes, teamLeaderReportRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/reminders`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/email-notifications`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/team-leader-report`, { withCredentials: true })
      ]);
      
      setTodayReminderTime(remindersRes.data.today_reminder_time);
      setFinalReminderTime(remindersRes.data.final_reminder_time);
      setTodayReminderEnabled(remindersRes.data.today_reminder_enabled !== false);
      setFinalReminderEnabled(remindersRes.data.final_reminder_enabled !== false);
      
      setCcRemindersToTeamLeader(notificationsRes.data.cc_reminders_to_team_leader);
      setCcRemindersToAdmin(notificationsRes.data.cc_reminders_to_admin);
      setCcRemindersToTaj(notificationsRes.data.cc_reminders_to_taj || false);
      setNotifyTeamLeaderOnSubmission(notificationsRes.data.notify_team_leader_on_submission);
      setNotifyAdminOnSubmission(notificationsRes.data.notify_admin_on_submission);
      setNotifyTajOnSubmission(notificationsRes.data.notify_taj_on_submission || false);
      setProductivityThreshold(notificationsRes.data.productivity_threshold || "");
      setProductivityAlertEmail(notificationsRes.data.productivity_alert_email || "taj@jobtalk.ai");
      
      setTeamLeaderReportEnabled(teamLeaderReportRes.data.enabled);
      setTeamLeaderReportTime(teamLeaderReportRes.data.report_time);
      setIncludeTasksToday(teamLeaderReportRes.data.include_tasks_today);
      setIncludeChallenges(teamLeaderReportRes.data.include_challenges);
      setIncludePlanTomorrow(teamLeaderReportRes.data.include_plan_tomorrow);
      setIncludeAiDiscovery(teamLeaderReportRes.data.include_ai_discovery ?? true);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  // Backup Settings Functions
  const fetchBackupSettings = async () => {
    setBackupSettingsLoading(true);
    try {
      const [settingsRes, listRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/backup/settings`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/backup/list`, { withCredentials: true })
      ]);
      setBackupSettings(settingsRes.data);
      setBackupList(listRes.data.backups || []);
    } catch (error) {
      console.error("Error fetching backup settings:", error);
    } finally {
      setBackupSettingsLoading(false);
    }
  };

  const handleSaveBackupSettings = async () => {
    setSavingBackupSettings(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/settings`,
        backupSettings,
        { withCredentials: true }
      );
      toast.success("Backup settings saved successfully");
    } catch (error) {
      console.error("Error saving backup settings:", error);
      toast.error("Failed to save backup settings");
    } finally {
      setSavingBackupSettings(false);
    }
  };

  const handleRunBackupNow = async () => {
    setRunningBackup(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/run-now`,
        {},
        { withCredentials: true }
      );
      toast.success(`Backup created: ${response.data.backup.filename}`);
      fetchBackupSettings(); // Refresh backup list
    } catch (error) {
      console.error("Error running backup:", error);
      toast.error(error.response?.data?.detail || "Failed to create backup");
    } finally {
      setRunningBackup(false);
    }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/download/${filename}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      console.error("Error downloading backup:", error);
      toast.error("Failed to download backup");
    }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(`Delete backup "${filename}"? This cannot be undone.`)) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/${filename}`,
        { withCredentials: true }
      );
      toast.success(`Deleted ${filename}`);
      fetchBackupSettings(); // Refresh backup list
    } catch (error) {
      console.error("Error deleting backup:", error);
      toast.error("Failed to delete backup");
    }
  };

  // Environment Variables Functions
  const fetchEnvVars = async () => {
    setLoadingEnvVars(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/env-vars`,
        { withCredentials: true }
      );
      const data = response.data;
      setEnvVars({
        backend: data.backend?.variables || {},
        frontend: data.frontend?.variables || {}
      });
      setEditedEnvVars({
        backend: { ...data.backend?.variables } || {},
        frontend: { ...data.frontend?.variables } || {}
      });
    } catch (error) {
      console.error("Error fetching env vars:", error);
      toast.error("Failed to load environment variables");
    } finally {
      setLoadingEnvVars(false);
    }
  };

  const handleOpenEnvVarsDialog = () => {
    setEnvVarsDialogOpen(true);
    fetchEnvVars();
  };

  const handleEnvVarChange = (env, key, value) => {
    setEditedEnvVars(prev => ({
      ...prev,
      [env]: {
        ...prev[env],
        [key]: value
      }
    }));
  };

  const handleSaveEnvVars = async (environment) => {
    setSavingEnvVars(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/env-vars`,
        {
          environment,
          variables: editedEnvVars[environment]
        },
        { withCredentials: true }
      );
      toast.success(`${environment} environment variables saved! Restart required for changes to take effect.`);
      // Update the original values
      setEnvVars(prev => ({
        ...prev,
        [environment]: { ...editedEnvVars[environment] }
      }));
    } catch (error) {
      console.error("Error saving env vars:", error);
      toast.error(error.response?.data?.detail || "Failed to save environment variables");
    } finally {
      setSavingEnvVars(false);
    }
  };

  const hasEnvChanges = (environment) => {
    const original = envVars[environment] || {};
    const edited = editedEnvVars[environment] || {};
    return JSON.stringify(original) !== JSON.stringify(edited);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleSaveReminders = async () => {
    setSavingReminders(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/reminders`,
        {
          today_reminder_time: todayReminderTime,
          final_reminder_time: finalReminderTime,
          today_reminder_enabled: todayReminderEnabled,
          final_reminder_enabled: finalReminderEnabled
        },
        { withCredentials: true }
      );
      toast.success("Reminder times saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingReminders(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/email-notifications`,
        {
          cc_reminders_to_team_leader: ccRemindersToTeamLeader,
          cc_reminders_to_admin: ccRemindersToAdmin,
          cc_reminders_to_taj: ccRemindersToTaj,
          notify_team_leader_on_submission: notifyTeamLeaderOnSubmission,
          notify_admin_on_submission: notifyAdminOnSubmission,
          notify_taj_on_submission: notifyTajOnSubmission,
          productivity_threshold: productivityThreshold ? parseInt(productivityThreshold) : null,
          productivity_alert_email: productivityAlertEmail
        },
        { withCredentials: true }
      );
      toast.success("Email notification settings saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleSaveTeamLeaderReport = async () => {
    setSavingTeamLeaderReport(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/team-leader-report`,
        {
          enabled: teamLeaderReportEnabled,
          report_time: teamLeaderReportTime,
          include_tasks_today: includeTasksToday,
          include_challenges: includeChallenges,
          include_plan_tomorrow: includePlanTomorrow,
          include_ai_discovery: includeAiDiscovery
        },
        { withCredentials: true }
      );
      toast.success("Team Leader Report settings saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingTeamLeaderReport(false);
    }
  };

  const openTestReportDialog = async () => {
    setTestReportDialogOpen(true);
    setLoadingTeamLeaders(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/team-leaders-with-teams`,
        { withCredentials: true }
      );
      // Sort team leaders alphabetically by name
      const sortedLeaders = response.data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setTeamLeaders(sortedLeaders);
      // Select all by default
      setSelectedTeamLeaders(sortedLeaders.map(tl => tl.id));
    } catch (error) {
      console.error("Error fetching team leaders:", error);
      toast.error("Failed to load team leaders");
    } finally {
      setLoadingTeamLeaders(false);
    }
  };

  const toggleTeamLeaderSelection = (leaderId) => {
    setSelectedTeamLeaders(prev => 
      prev.includes(leaderId)
        ? prev.filter(id => id !== leaderId)
        : [...prev, leaderId]
    );
  };

  const selectAllTeamLeaders = () => {
    setSelectedTeamLeaders(teamLeaders.map(tl => tl.id));
  };

  const deselectAllTeamLeaders = () => {
    setSelectedTeamLeaders([]);
  };

  const handleGenerateTestReport = async () => {
    if (selectedTeamLeaders.length === 0) {
      toast.error("Please select at least one team leader");
      return;
    }
    
    if (!includeTasksToday && !includeChallenges && !includePlanTomorrow) {
      toast.error("Please select at least one report content option above");
      return;
    }

    setGeneratingReport(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/team-leader-report/test`,
        {
          team_leader_ids: selectedTeamLeaders,
          include_tasks_today: includeTasksToday,
          include_challenges: includeChallenges,
          include_plan_tomorrow: includePlanTomorrow,
          include_ai_discovery: includeAiDiscovery,
          send_email_to_leaders: sendReportToLeaders
        },
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `team_leader_report_test_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      
      if (sendReportToLeaders) {
        toast.success("Test report generated, downloaded, and emailed to team leaders!");
      } else {
        toast.success("Test report generated and downloaded!");
      }
      setTestReportDialogOpen(false);
      setSendReportToLeaders(false); // Reset checkbox
    } catch (error) {
      console.error("Error generating test report:", error);
      toast.error(error.response?.data?.detail || "Failed to generate test report");
    } finally {
      setGeneratingReport(false);
    }
  };

  // Export all tables as a SINGLE combined backup file (recommended for easy import)
  const handleExportCombined = async () => {
    setExporting(true);
    const exportCollections = ['users', 'daily_logs', 'teams', 'forms', 'invites', 'email_logs', 'settings'];
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `dailylog_full_backup_${timestamp}.json`;
    
    try {
      const combinedBackup = {
        backup_type: "combined",
        created_at: new Date().toISOString(),
        version: "1.0",
        collections: {}
      };

      for (const collection of exportCollections) {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${collection}`,
          { withCredentials: true }
        );
        combinedBackup.collections[collection] = response.data;
      }
      
      const blob = new Blob([JSON.stringify(combinedBackup, null, 2)], { type: 'application/json' });
      
      // Try to use File System Access API to let user choose folder
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'JSON Files',
              accept: { 'application/json': ['.json'] }
            }]
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success(`Backup saved successfully!`);
          setExportDialogOpen(false);
          return;
        } catch (err) {
          // User cancelled or API not fully supported, fall back to download
          if (err.name !== 'AbortError') {
            console.log('File System API not available, using download fallback');
          } else {
            setExporting(false);
            return; // User cancelled
          }
        }
      }
      
      // Fallback: Download to default location
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success("Full backup exported successfully! Use this file for one-click import.");
      setExportDialogOpen(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error.response?.data?.detail || "Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  // Export all tables as separate JSON files
  const handleExportAll = async () => {
    setExporting(true);
    const exportCollections = ['users', 'daily_logs', 'teams', 'forms', 'invites', 'email_logs', 'settings'];
    const timestamp = new Date().toISOString().split('T')[0];
    
    try {
      // Try to use File System Access API to let user choose folder
      if ('showDirectoryPicker' in window) {
        try {
          const dirHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
          
          for (const collection of exportCollections) {
            const response = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${collection}`,
              { withCredentials: true }
            );
            
            const filename = `${collection}_backup_${timestamp}.json`;
            const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(JSON.stringify(response.data, null, 2));
            await writable.close();
          }
          
          toast.success(`All tables exported to selected folder!`);
          setExportDialogOpen(false);
          return;
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.log('Directory picker not available, using download fallback');
          } else {
            setExporting(false);
            return; // User cancelled
          }
        }
      }
      
      // Fallback: Download each file separately
      for (const collection of exportCollections) {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${collection}`,
          { withCredentials: true }
        );
        
        // Create and download JSON file
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${collection}_backup_${timestamp}.json`;
        link.click();
        URL.revokeObjectURL(url);
        
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      toast.success("All tables exported as separate files!");
      setExportDialogOpen(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error.response?.data?.detail || "Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  // Export single table
  const handleExportTable = async (collection) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${collection}`,
        { withCredentials: true }
      );
      
      const timestamp = new Date().toISOString().split('T')[0];
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${collection}_backup_${timestamp}.json`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success(`${collection} exported successfully!`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error(`Failed to export ${collection}`);
    }
  };

  // Handle file selection for import
  const handleFileSelect = (collection, event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImportFiles(prev => ({ ...prev, [collection]: file }));
    }
  };

  // Clear selected file
  const clearSelectedFile = (collection) => {
    setSelectedImportFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[collection];
      return newFiles;
    });
    if (fileInputRefs[collection]?.current) {
      fileInputRefs[collection].current.value = '';
    }
  };

  // Handle combined backup file selection
  const handleCombinedFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCombinedBackupFile(file);
    }
  };

  // Select folder and find backup files using File System Access API
  const handleSelectFolder = async () => {
    if (!('showDirectoryPicker' in window)) {
      toast.error("Folder selection is not supported in this browser. Please use Chrome or Edge.");
      return;
    }

    try {
      const dirHandle = await window.showDirectoryPicker({ mode: 'read' });
      setSelectedFolderName(dirHandle.name);
      
      const foundFiles = [];
      
      // Scan for JSON backup files
      for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file' && entry.name.endsWith('.json')) {
          const file = await entry.getFile();
          const content = await file.text();
          
          try {
            const data = JSON.parse(content);
            // Check if it's a combined backup
            if (data.backup_type === "combined") {
              foundFiles.push({
                type: 'combined',
                name: entry.name,
                file: file,
                created_at: data.created_at,
                collections: Object.keys(data.collections || {})
              });
            } 
            // Check if it's an individual table backup
            else if (Array.isArray(data) || (data.data && Array.isArray(data.data))) {
              // Try to determine which collection it is from filename
              const collectionMatch = entry.name.match(/^(users|daily_logs|teams|forms|invites|email_logs|settings)_backup/);
              if (collectionMatch) {
                foundFiles.push({
                  type: 'individual',
                  collection: collectionMatch[1],
                  name: entry.name,
                  file: file
                });
              }
            }
          } catch (e) {
            // Not a valid JSON or not a backup file, skip
          }
        }
      }
      
      setFolderFiles(foundFiles);
      
      if (foundFiles.length === 0) {
        toast.info("No backup files found in selected folder");
      } else {
        // Auto-select combined backup if found
        const combinedFile = foundFiles.find(f => f.type === 'combined');
        if (combinedFile) {
          setCombinedBackupFile(combinedFile.file);
          toast.success(`Found combined backup: ${combinedFile.name}`);
        } else {
          toast.success(`Found ${foundFiles.length} backup file(s) in folder`);
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error("Folder selection error:", err);
        toast.error("Failed to access folder");
      }
    }
  };

  // Import from combined backup file (one-click import all)
  const handleImportCombined = async () => {
    if (!combinedBackupFile) {
      toast.error("Please select a backup file first");
      return;
    }

    setImporting(true);
    setImportResult(null);
    const results = {};

    try {
      const fileContent = await combinedBackupFile.text();
      const backupData = JSON.parse(fileContent);
      
      // Validate it's a combined backup
      if (backupData.backup_type !== "combined" || !backupData.collections) {
        toast.error("Invalid backup file. Please select a combined backup file (dailylog_full_backup_*.json)");
        setImporting(false);
        return;
      }

      // Import each collection
      for (const [collection, collectionData] of Object.entries(backupData.collections)) {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/import/${collection}`,
            { data: collectionData },
            { withCredentials: true }
          );
          results[collection] = { success: true, count: response.data.imported_count };
        } catch (err) {
          results[collection] = { success: false, error: err.response?.data?.detail || err.message };
        }
      }
      
      setImportResult(results);
      
      const successCount = Object.values(results).filter(r => r.success).length;
      const totalCount = Object.keys(results).length;
      
      if (successCount === totalCount) {
        toast.success(`All ${totalCount} tables imported successfully!`);
      } else if (successCount > 0) {
        toast.warning(`${successCount}/${totalCount} tables imported with some errors`);
      } else {
        toast.error("Import failed");
      }
      
      // Clear the file selection
      setCombinedBackupFile(null);
      if (combinedFileInputRef.current) {
        combinedFileInputRef.current.value = '';
      }
    } catch (error) {
      console.error("Import error:", error);
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON file. Please select a valid backup file.");
      } else {
        toast.error("Import failed: " + error.message);
      }
    } finally {
      setImporting(false);
    }
  };

  // Import selected files (individual tables)
  const handleImportFiles = async () => {
    const filesToImport = Object.entries(selectedImportFiles);
    if (filesToImport.length === 0) {
      toast.error("Please select at least one file to import");
      return;
    }

    setImporting(true);
    setImportResult(null);
    const results = {};

    try {
      for (const [collection, file] of filesToImport) {
        try {
          const fileContent = await file.text();
          const jsonData = JSON.parse(fileContent);
          
          const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/import/${collection}`,
            { data: jsonData },
            { withCredentials: true }
          );
          
          results[collection] = { success: true, count: response.data.imported_count };
        } catch (err) {
          results[collection] = { success: false, error: err.response?.data?.detail || err.message };
        }
      }
      
      setImportResult(results);
      
      const successCount = Object.values(results).filter(r => r.success).length;
      if (successCount === filesToImport.length) {
        toast.success("All files imported successfully!");
      } else if (successCount > 0) {
        toast.warning("Some files imported with errors");
      } else {
        toast.error("Import failed");
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };


  // Bulk Delete Functions
  const LEAD_FIELDS_FOR_FILTER = [
    { value: 'work_email', label: 'Work Email' },
    { value: 'personal_email', label: 'Personal Email' },
    { value: 'work_direct_phone', label: 'Work Phone' },
    { value: 'mobile_phone', label: 'Mobile Phone' },
    { value: 'linkedin_url', label: 'LinkedIn URL' },
    { value: 'x_twitter_url', label: 'X/Twitter URL' },
    { value: 'linkedin_url_x', label: 'LinkedIn URL-X' },
    { value: 'first_name', label: 'First Name' },
    { value: 'last_name', label: 'Last Name' },
    { value: 'title', label: 'Title' },
    { value: 'company_name', label: 'Company Name' },
    { value: 'status', label: 'Status' },
    { value: 'source', label: 'Source' },
    { value: 'batch_name', label: 'Batch Name' },
    { value: 'dnc', label: 'Do Not Call' },
    { value: 'dne', label: 'Do Not Email' },
    { value: 'created_at', label: 'Created Date' },
    { value: 'updated_at', label: 'Updated Date' }
  ];

  const OPERATORS = [
    { value: 'is_null', label: 'Is NULL or Blank' },
    { value: 'is_not_null', label: 'Is NOT NULL' },
    { value: 'equals', label: '= (equals)' },
    { value: 'not_equals', label: '<> (not equals)' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does Not Contain' }
  ];

  const addCondition = () => {
    setDeleteConditions([...deleteConditions, { field: 'work_email', operator: 'is_null', value: '' }]);
  };

  const removeCondition = (index) => {
    if (deleteConditions.length === 1) {
      toast.error('At least one condition is required');
      return;
    }
    setDeleteConditions(deleteConditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index, key, value) => {
    const updated = [...deleteConditions];
    updated[index][key] = value;
    setDeleteConditions(updated);
  };

  const handlePreviewDelete = async () => {
    setLoadingPreview(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bulk-delete/preview`,
        { conditions: deleteConditions },
        { withCredentials: true }
      );
      setDeletePreviewCount(response.data.count);
      toast.success(`Found ${response.data.count} leads matching criteria`);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!deletePreviewCount || deletePreviewCount === 0) {
      toast.error('No leads to delete. Run preview first.');
      return;
    }

    if (!window.confirm(`⚠️ WARNING: This will DELETE ${deletePreviewCount} leads permanently. This action cannot be undone. Continue?`)) {
      return;
    }

    setDeletingLeads(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bulk-delete/leads`,
        { 
          conditions: deleteConditions,
          delete_orphaned_companies: deleteCompaniesWithNoLeads
        },
        { withCredentials: true }
      );
      
      toast.success(`Deleted ${response.data.leads_deleted} leads and ${response.data.companies_deleted} orphaned companies`);
      setBulkDeleteDialogOpen(false);
      setDeleteConditions([{ field: 'work_email', operator: 'is_null', value: '' }]);
      setDeletePreviewCount(null);
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete leads');
    } finally {
      setDeletingLeads(false);
    }
  };

  const handleDeleteCompaniesWithNoLeads = async () => {
    if (!window.confirm('⚠️ WARNING: This will DELETE all companies that have NO leads. This action cannot be undone. Continue?')) {
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/delete-empty-companies`,
        {},
        { withCredentials: true }
      );
      toast.success(`Deleted ${response.data.deleted} companies with no leads`);
    } catch (error) {
      console.error('Delete empty companies error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete companies');
    }
  };

  // Smart Bulk Delete Functions
  const handleSmartDeletePreview = async () => {
    setLoadingSmartPreview(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smart-delete/preview`,
        {},
        { withCredentials: true }
      );
      setSmartDeletePreview(response.data);
      toast.success(`Found ${response.data.leads_count} leads with no valid contact info`);
    } catch (error) {
      console.error('Smart delete preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview');
    } finally {
      setLoadingSmartPreview(false);
    }
  };

  const handleSmartDelete = async () => {
    if (!smartDeletePreview || smartDeletePreview.leads_count === 0) {
      toast.error('No leads to delete. Run preview first.');
      return;
    }

    if (!window.confirm(`⚠️ SMART DELETE WARNING\n\nThis will DELETE:\n• ${smartDeletePreview.leads_count} leads with NO valid email AND NO valid phone\n• ${smartDeletePreview.orphaned_companies_count} companies that will have no remaining leads\n\nThis action cannot be undone. Continue?`)) {
      return;
    }

    setExecutingSmartDelete(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smart-delete/execute`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`Smart Delete Complete: ${response.data.leads_deleted} leads and ${response.data.companies_deleted} companies deleted`);
      setSmartDeletePreview(null);
    } catch (error) {
      console.error('Smart delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to execute smart delete');
    } finally {
      setExecutingSmartDelete(false);
    }
  };

  // Selective Export/Import Functions
  const AVAILABLE_COLLECTIONS = [
    { id: 'users', name: 'Users', icon: Users, description: 'User accounts and profiles' },
    { id: 'teams', name: 'Teams', icon: Building2, description: 'Team configurations' },
    { id: 'daily_logs', name: 'Daily Logs', icon: FileText, description: 'All daily log entries' },
    { id: 'forms', name: 'Forms', icon: FormInput, description: 'Custom form definitions' },
    { id: 'companies', name: 'Companies', icon: Building2, description: 'CRM companies with notes' },
    { id: 'leads', name: 'Leads', icon: Users, description: 'CRM leads with social URLs' },
    { id: 'lead_notes', name: 'Lead Notes', icon: FileText, description: 'Notes on leads' },
    { id: 'tasks', name: 'Tasks', icon: CheckCircle, description: 'CRM tasks' },
    { id: 'crm_communications', name: 'Communications', icon: Mail, description: 'Emails, SMS, calls' },
    { id: 'crm_templates', name: 'Templates', icon: File, description: 'Email/SMS templates' },
    { id: 'email_accounts', name: 'Email Accounts', icon: Mail, description: 'Connected email accounts' },
    { id: 'settings', name: 'Settings', icon: Settings, description: 'Application settings' },
    { id: 'hr_data', name: 'HR Data', icon: Users, description: 'Employee HR information' }
  ];

  const toggleCollection = (collectionId) => {
    setSelectedCollections(prev => 
      prev.includes(collectionId) 
        ? prev.filter(id => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  const selectAllCollections = () => {
    setSelectedCollections(AVAILABLE_COLLECTIONS.map(c => c.id));
  };

  const deselectAllCollections = () => {
    setSelectedCollections([]);
  };

  const handleExportSelected = async () => {
    if (selectedCollections.length === 0) {
      toast.error('Please select at least one collection to export');
      return;
    }

    setExportingSelected(true);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `selected_backup_${timestamp}.json`;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/export-selected`,
        { collections: selectedCollections },
        { withCredentials: true }
      );

      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);

      toast.success(`Exported ${selectedCollections.length} collections successfully!`);
      setSelectiveExportDialogOpen(false);
      setSelectedCollections([]);
    } catch (error) {
      console.error('Export selected error:', error);
      toast.error(error.response?.data?.detail || 'Failed to export selected collections');
    } finally {
      setExportingSelected(false);
    }
  };

  const handleImportSelected = async () => {
    if (!selectiveImportFile) {
      toast.error('Please select a file to import');
      return;
    }

    setImportingSelected(true);

    try {
      const fileContent = await selectiveImportFile.text();
      const data = JSON.parse(fileContent);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/import-selected`,
        { data },
        { withCredentials: true }
      );

      setSelectiveImportResult(response.data);
      toast.success('Import completed!');
    } catch (error) {
      console.error('Import selected error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import');
    } finally {
      setImportingSelected(false);
    }
  };

  // Sync Database Functions
  const handleTestSyncConnection = async () => {
    if (!syncConnectionString || !syncDatabaseName) {
      toast.error('Please enter both connection string and database name');
      return;
    }

    setTestingSyncConnection(true);
    setSyncConnectionTestResult(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/test-connection`,
        {
          connection_string: syncConnectionString,
          database_name: syncDatabaseName
        },
        { withCredentials: true }
      );

      setSyncConnectionTestResult(response.data);
      if (response.data.success) {
        toast.success('Connection successful!');
      } else {
        toast.error('Connection failed');
      }
    } catch (error) {
      console.error('Connection test error:', error);
      setSyncConnectionTestResult({
        success: false,
        message: error.response?.data?.detail || 'Connection test failed'
      });
      toast.error('Connection test failed');
    } finally {
      setTestingSyncConnection(false);
    }
  };

  const handleSyncDatabase = async () => {
    if (!syncConnectionString || !syncDatabaseName) {
      toast.error('Please enter connection details and test connection first');
      return;
    }

    if (!syncConnectionTestResult || !syncConnectionTestResult.success) {
      toast.error('Please test connection successfully before syncing');
      return;
    }

    const confirmMsg = syncMode === 'merge' 
      ? '⚠️ MERGE SYNC: This will add new fields and tables to the target database without overriding existing data. Continue?'
      : '⚠️ OVERRIDE SYNC: This will REPLACE all data in the target database with data from this database. This action cannot be undone. Continue?';

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setSyncingDatabase(true);
    setSyncResult(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/sync-database`,
        {
          connection_string: syncConnectionString,
          database_name: syncDatabaseName,
          sync_mode: syncMode
        },
        { withCredentials: true }
      );

      setSyncResult(response.data);
      toast.success(`Database sync complete! ${response.data.collections_synced} collections synced.`);
    } catch (error) {
      console.error('Database sync error:', error);
      toast.error(error.response?.data?.detail || 'Database sync failed');
    } finally {
      setSyncingDatabase(false);
    }
  };

  const collections = [
    { id: 'users', name: 'Users', icon: Users, description: 'User accounts and profiles' },
    { id: 'daily_logs', name: 'Daily Logs', icon: FileText, description: 'All daily log entries' },
    { id: 'teams', name: 'Teams', icon: Building2, description: 'Team configurations' },
    { id: 'forms', name: 'Forms', icon: FormInput, description: 'Custom form definitions' },
    { id: 'invites', name: 'Invites', icon: Mail, description: 'Pending invitations' },
    { id: 'email_logs', name: 'Email Logs', icon: History, description: 'Sent email history' },
    { id: 'settings', name: 'Settings', icon: Settings, description: 'Application settings' }
  ];

  // CRM Export function
  const handleCrmExport = async () => {
    setCrmExporting(true);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `crm_full_backup_${timestamp}.json`;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/export-crm`,
        { withCredentials: true }
      );
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      
      // Try to use File System Access API
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'JSON Files',
              accept: { 'application/json': ['.json'] }
            }]
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success('CRM backup saved successfully!');
          setCrmExportDialogOpen(false);
          return;
        } catch (err) {
          if (err.name === 'AbortError') {
            setCrmExporting(false);
            return;
          }
        }
      }
      
      // Fallback download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success('CRM data exported successfully!');
      setCrmExportDialogOpen(false);
    } catch (error) {
      console.error("CRM Export error:", error);
      toast.error(error.response?.data?.detail || "Failed to export CRM data");
    } finally {
      setCrmExporting(false);
    }
  };

  // CRM Import function
  const handleCrmImport = async () => {
    if (!crmBackupFile) {
      toast.error("Please select a CRM backup file first");
      return;
    }

    setCrmImporting(true);
    setCrmImportResult(null);

    try {
      const fileContent = await crmBackupFile.text();
      const backupData = JSON.parse(fileContent);
      
      if (backupData.backup_type !== "crm_data") {
        toast.error("Invalid backup file. Please select a CRM backup file (crm_full_backup_*.json)");
        setCrmImporting(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/import-crm`,
        { data: backupData },
        { withCredentials: true }
      );
      
      setCrmImportResult(response.data.results);
      
      const successCount = Object.values(response.data.results).filter(r => r.success).length;
      const totalCount = Object.keys(response.data.results).length;
      
      if (successCount === totalCount) {
        toast.success(`All ${totalCount} CRM tables imported successfully!`);
      } else if (successCount > 0) {
        toast.warning(`${successCount}/${totalCount} tables imported with some errors`);
      } else {
        toast.error("Import failed");
      }
      
      setCrmBackupFile(null);
      if (crmFileInputRef.current) crmFileInputRef.current.value = '';
    } catch (error) {
      console.error("CRM Import error:", error);
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON file. Please select a valid CRM backup file.");
      } else {
        toast.error(error.response?.data?.detail || "Import failed");
      }
    } finally {
      setCrmImporting(false);
    }
  };

  // ===== NEW UNIFIED BACKUP FUNCTIONS =====
  
  // Export ALL Data - Single JSON for all database collections
  const handleExportAllData = async () => {
    setExportingAll(true);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `dailylog_complete_backup_${timestamp}.json`;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/export-all`,
        { withCredentials: true }
      );
      
      // Extract collection stats for summary
      const collections = response.data.collections || {};
      const exportStats = Object.entries(collections).map(([name, info]) => ({
        name,
        count: info.count || 0
      })).sort((a, b) => b.count - a.count);
      
      const totalRows = exportStats.reduce((sum, item) => sum + item.count, 0);
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      
      // Try File System Access API first
      let saved = false;
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: 'JSON Files', accept: { 'application/json': ['.json'] } }]
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          saved = true;
        } catch (err) {
          if (err.name === 'AbortError') {
            setExportingAll(false);
            return;
          }
        }
      }
      
      // Fallback download
      if (!saved) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
      }
      
      // Show export results dialog
      setExportResultData({
        type: 'export',
        filename,
        totalTables: exportStats.length,
        totalRows,
        collections: exportStats,
        createdAt: response.data.created_at
      });
      setExportResultDialogOpen(true);
      toast.success('Complete database backup exported successfully!');
    } catch (error) {
      console.error("Export ALL error:", error);
      toast.error(error.response?.data?.detail || "Failed to export data");
    } finally {
      setExportingAll(false);
    }
  };

  // Export ENV Data - Separate JSON for environment variables
  const handleExportEnvData = async () => {
    setExportingEnv(true);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `dailylog_env_backup_${timestamp}.json`;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/export-env`,
        { withCredentials: true }
      );
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      
      // Try File System Access API first
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: 'JSON Files', accept: { 'application/json': ['.json'] } }]
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success('Environment configuration exported successfully!');
          return;
        } catch (err) {
          if (err.name === 'AbortError') {
            setExportingEnv(false);
            return;
          }
        }
      }
      
      // Fallback download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success('Environment configuration exported!');
    } catch (error) {
      console.error("Export ENV error:", error);
      toast.error("Failed to export ENV");
    } finally {
      setExportingEnv(false);
    }
  };

  // Export FORMS - Separate JSON for forms only
  const handleExportForms = async () => {
    setExportingForms(true);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `dailylog_forms_${timestamp}.json`;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/export-forms`,
        { withCredentials: true }
      );
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Forms exported successfully! (${response.data.forms_count} forms)`);
    } catch (error) {
      console.error("Export FORMS error:", error);
      toast.error(error.response?.data?.detail || "Failed to export forms");
    } finally {
      setExportingForms(false);
    }
  };

  // Import ALL Data - with warning confirmation
  const handleImportAllData = async () => {
    if (!importAllFile) {
      toast.error("Please select a backup file first");
      return;
    }
    
    // Show warning dialog if not already confirmed
    if (!showImportWarning) {
      setShowImportWarning(true);
      return;
    }

    setImportingAll(true);
    setImportAllResult(null);

    try {
      const fileContent = await importAllFile.text();
      const backupData = JSON.parse(fileContent);
      
      // Validate backup file
      if (backupData.backup_type !== "complete_backup") {
        toast.error("Invalid backup file. Please select a complete backup file (dailylog_complete_backup_*.json)");
        setImportingAll(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/import-all`,
        backupData,
        { withCredentials: true }
      );
      
      // Build import stats for display
      const results = response.data.results || {};
      const importStats = Object.entries(results).map(([name, info]) => ({
        name,
        count: info.inserted || info.count || 0,
        success: info.success !== false
      })).sort((a, b) => b.count - a.count);
      
      const totalRows = importStats.reduce((sum, item) => sum + item.count, 0);
      const successCount = importStats.filter(i => i.success).length;
      
      // Close import dialog and show results dialog
      setImportAllDialogOpen(false);
      setExportResultData({
        type: 'import',
        totalTables: importStats.length,
        totalRows,
        successCount,
        collections: importStats,
        backupDate: response.data.backup_date
      });
      setExportResultDialogOpen(true);
      toast.success(response.data.message || "Data imported successfully!");
      
    } catch (error) {
      console.error("Import ALL error:", error);
      if (error instanceof SyntaxError) {
        setImportAllResult({ success: false, message: "Invalid JSON file. Please select a valid backup file." });
        toast.error("Invalid JSON file");
      } else {
        setImportAllResult({ success: false, message: error.response?.data?.detail || "Import failed" });
        toast.error(error.response?.data?.detail || "Import failed");
      }
    } finally {
      setImportingAll(false);
    }
  };

  // Import FORMS handler
  const handleImportForms = async () => {
    if (!importFormsFile) {
      toast.error("Please select a forms file to import");
      return;
    }

    setImportingForms(true);
    try {
      const text = await importFormsFile.text();
      const formsData = JSON.parse(text);
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/import-forms`,
        formsData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      setImportFormsResult(response.data);
      toast.success(response.data.message || 'Forms imported successfully!');
    } catch (error) {
      console.error("Import FORMS error:", error);
      const errorMsg = error.response?.data?.detail || "Failed to import forms";
      toast.error(errorMsg);
      setImportFormsResult({ error: errorMsg });
    } finally {
      setImportingForms(false);
    }
  };

  // Backup Settings handlers
  const confirmImport = () => {
    handleImportAllData();
  };

  // ===== DATABASE MIGRATION FUNCTIONS =====
  
  // Export migration package (with schema + indexes + data)
  const handleExportMigration = async () => {
    setExportingMigration(true);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `dailylog_migration_package_${timestamp}.json`;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/export-migration`,
        { withCredentials: true }
      );
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      
      // Try File System Access API first
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: 'JSON Files', accept: { 'application/json': ['.json'] } }]
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success('Migration package exported successfully!');
          setMigrationDialogOpen(false);
          return;
        } catch (err) {
          if (err.name === 'AbortError') {
            setExportingMigration(false);
            return;
          }
        }
      }
      
      // Fallback download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success('Migration package exported! Use this file to set up your new database.');
      setMigrationDialogOpen(false);
    } catch (error) {
      console.error("Export migration error:", error);
      toast.error(error.response?.data?.detail || "Failed to export migration package");
    } finally {
      setExportingMigration(false);
    }
  };
  
  // Test connection to new database
  const handleTestConnection = async () => {
    if (!newDbConnectionString.trim() || !newDbName.trim()) {
      toast.error("Please enter both connection string and database name");
      return;
    }
    
    setTestingConnection(true);
    setConnectionTestResult(null);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/test-connection`,
        {
          connection_string: newDbConnectionString,
          database_name: newDbName
        },
        { withCredentials: true }
      );
      
      setConnectionTestResult(response.data);
      if (response.data.success) {
        toast.success("Connection successful!");
      } else {
        toast.error(response.data.message || "Connection failed");
      }
    } catch (error) {
      console.error("Connection test error:", error);
      setConnectionTestResult({
        success: false,
        message: error.response?.data?.detail || "Connection test failed"
      });
      toast.error(error.response?.data?.detail || "Connection test failed");
    } finally {
      setTestingConnection(false);
    }
  };
  
  // Migrate data to new database
  const handleMigrateToNewDatabase = async () => {
    if (!migrationFile) {
      toast.error("Please select a migration package file");
      return;
    }
    
    if (!newDbConnectionString.trim() || !newDbName.trim()) {
      toast.error("Please enter connection details and test the connection first");
      return;
    }
    
    if (!connectionTestResult?.success) {
      toast.error("Please test the connection first and ensure it's successful");
      return;
    }
    
    setMigratingData(true);
    setMigrationResult(null);
    
    try {
      const fileContent = await migrationFile.text();
      const backupData = JSON.parse(fileContent);
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/migrate-to-new-database`,
        {
          connection_string: newDbConnectionString,
          database_name: newDbName,
          backup_data: backupData,
          drop_existing: true
        },
        { withCredentials: true }
      );
      
      setMigrationResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message || "Migration completed successfully!");
      } else {
        toast.warning("Migration completed with some errors");
      }
    } catch (error) {
      console.error("Migration error:", error);
      if (error instanceof SyntaxError) {
        setMigrationResult({ success: false, message: "Invalid JSON file" });
        toast.error("Invalid migration package file");
      } else {
        setMigrationResult({ 
          success: false, 
          message: error.response?.data?.detail || "Migration failed" 
        });
        toast.error(error.response?.data?.detail || "Migration failed");
      }
    } finally {
      setMigratingData(false);
    }
  };
  
  // Reset migration dialog state
  const resetMigrationDialog = () => {
    setMigrationStep(1);
    setMigrationMode(null);
    setNewDbConnectionString('');
    setNewDbName('');
    setConnectionTestResult(null);
    setMigrationFile(null);
    setMigrationResult(null);
    if (migrationFileInputRef.current) migrationFileInputRef.current.value = '';
  };

  const formatTimeDisplay = (time) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm} ET`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Settings className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-500">Configure system settings</p>
        </div>
      </div>

      {/* Email Reminder Times */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-slate-600" />
            <CardTitle>Email Reminder Times</CardTitle>
          </div>
          <CardDescription>
            Configure when email reminders are sent to users who haven't submitted their logs.
            All times are in Eastern Time (ET) and reminders are sent on weekdays only.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Today's Reminder */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Checkbox
                id="today-reminder-enabled"
                checked={todayReminderEnabled}
                onCheckedChange={setTodayReminderEnabled}
              />
              <Label htmlFor="today-reminder-enabled" className="text-sm font-medium cursor-pointer">
                Today's Log Reminder
              </Label>
            </div>
            <p className="text-sm text-slate-500 mb-2">
              Sent to users who haven't submitted today's log
            </p>
            {todayReminderEnabled && (
              <div className="flex items-center gap-4 ml-8">
                <input
                  id="today-reminder"
                  type="time"
                  value={todayReminderTime}
                  onChange={(e) => setTodayReminderTime(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <span className="text-sm text-slate-500">
                  Currently: {formatTimeDisplay(todayReminderTime)}
                </span>
              </div>
            )}
          </div>

          {/* Final Reminder */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Checkbox
                id="final-reminder-enabled"
                checked={finalReminderEnabled}
                onCheckedChange={setFinalReminderEnabled}
              />
              <Label htmlFor="final-reminder-enabled" className="text-sm font-medium cursor-pointer">
                Final Reminder (Previous Day)
              </Label>
            </div>
            <p className="text-sm text-slate-500 mb-2">
              Final reminder for users who haven't submitted the previous day's log
            </p>
            {finalReminderEnabled && (
              <div className="flex items-center gap-4 ml-8">
                <input
                  id="final-reminder"
                  type="time"
                  value={finalReminderTime}
                  onChange={(e) => setFinalReminderTime(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <span className="text-sm text-slate-500">
                  Currently: {formatTimeDisplay(finalReminderTime)}
                </span>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="pt-4 flex justify-end">
            <Button onClick={handleSaveReminders} disabled={savingReminders}>
              {savingReminders ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Reminder Times
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email Notification Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-slate-600" />
            <CardTitle>Email Notification Settings</CardTitle>
          </div>
          <CardDescription>
            Configure who receives email notifications for reminders and log submissions.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* CC Reminders */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">CC Reminder Emails To:</Label>
              <p className="text-sm text-slate-500 mb-3">
                When sending reminder emails to users, also CC the following recipients
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="cc-team-leader"
                    checked={ccRemindersToTeamLeader}
                    onCheckedChange={setCcRemindersToTeamLeader}
                  />
                  <Label htmlFor="cc-team-leader" className="text-sm font-normal cursor-pointer">
                    Team Leader (of the user receiving the reminder)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="cc-admin"
                    checked={ccRemindersToAdmin}
                    onCheckedChange={setCcRemindersToAdmin}
                  />
                  <Label htmlFor="cc-admin" className="text-sm font-normal cursor-pointer">
                    Admin
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="cc-taj"
                    checked={ccRemindersToTaj}
                    onCheckedChange={setCcRemindersToTaj}
                  />
                  <Label htmlFor="cc-taj" className="text-sm font-normal cursor-pointer">
                    taj@jobtalk.ai
                  </Label>
                </div>
              </div>
            </div>
          </div>

          <hr className="border-slate-200" />

          {/* Log Submission Notifications */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Receive Email When User Submits Daily Log:
              </Label>
              <p className="text-sm text-slate-500 mb-3">
                Send notification emails when a user submits their daily log
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="notify-team-leader"
                    checked={notifyTeamLeaderOnSubmission}
                    onCheckedChange={setNotifyTeamLeaderOnSubmission}
                  />
                  <Label htmlFor="notify-team-leader" className="text-sm font-normal cursor-pointer">
                    Team Leader (of the user who submitted)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="notify-admin"
                    checked={notifyAdminOnSubmission}
                    onCheckedChange={setNotifyAdminOnSubmission}
                  />
                  <Label htmlFor="notify-admin" className="text-sm font-normal cursor-pointer">
                    Admin
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="notify-taj"
                    checked={notifyTajOnSubmission}
                    onCheckedChange={setNotifyTajOnSubmission}
                  />
                  <Label htmlFor="notify-taj" className="text-sm font-normal cursor-pointer">
                    taj@jobtalk.ai
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Team Leader notifications are sent only to the team leader(s) 
              of the specific user&apos;s team(s). All notification settings default to unchecked.
            </p>
          </div>

          {/* Productivity Monitoring */}
          <div className="pt-4 border-t space-y-4">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Productivity Monitoring (Recruiters Only)
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="productivity-threshold">Productivity Rating Threshold</Label>
                <Input
                  id="productivity-threshold"
                  type="number"
                  min="1"
                  max="10"
                  placeholder="e.g., 5"
                  value={productivityThreshold}
                  onChange={(e) => setProductivityThreshold(e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  Send alert if rating is below this value (1-10). Leave empty to disable.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="productivity-alert-email">Alert Email Address</Label>
                <Input
                  id="productivity-alert-email"
                  type="email"
                  placeholder="taj@jobtalk.ai"
                  value={productivityAlertEmail}
                  onChange={(e) => setProductivityAlertEmail(e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  Email to receive low productivity alerts
                </p>
              </div>
            </div>
            
            {productivityThreshold && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Active:</strong> Alert will be sent to <strong>{productivityAlertEmail}</strong> when any recruiter submits a productivity rating below <strong>{productivityThreshold}</strong>
                </p>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="pt-4 flex justify-end">
            <Button onClick={handleSaveNotifications} disabled={savingNotifications}>
              {savingNotifications ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Notification Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Manager Reports Scheduling */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-600" />
            <CardTitle>Automated Manager Reports</CardTitle>
          </div>
          <CardDescription>
            Schedule automated manager reports to be sent daily. Each manager receives their team&apos;s report with form-based fields. All reports CC&apos;d to taj@jobtalk.ai.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <Checkbox
              id="enable-manager-reports"
              checked={teamLeaderReportEnabled}
              onCheckedChange={setTeamLeaderReportEnabled}
            />
            <Label htmlFor="enable-manager-reports" className="text-sm font-medium cursor-pointer">
              Enable Automated Manager Reports
            </Label>
          </div>

          {teamLeaderReportEnabled && (
            <div className="space-y-4 pl-4 border-l-2 border-purple-200">
              <div className="space-y-2">
                <Label htmlFor="manager-report-time">Time to Send Report</Label>
                <Select value={teamLeaderReportTime} onValueChange={setTeamLeaderReportTime}>
                  <SelectTrigger id="manager-report-time" className="w-48">
                    <Clock className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="08:00">08:00 AM ET</SelectItem>
                    <SelectItem value="09:00">09:00 AM ET</SelectItem>
                    <SelectItem value="10:00">10:00 AM ET</SelectItem>
                    <SelectItem value="11:00">11:00 AM ET</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Reports will be sent at this time on weekdays (Monday-Friday). Each manager gets their team&apos;s data only.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  📋 <strong>What&apos;s Included:</strong> Each manager report uses form fields assigned to their team members. Reports are sent individually to each manager with CC to taj@jobtalk.ai.
                </p>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="pt-4 flex justify-end">
            <Button onClick={handleSaveTeamLeaderReport} disabled={savingTeamLeaderReport}>
              {savingTeamLeaderReport ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Manager Report Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>


      {/* Bulk Delete Leads */}
      <Card className="border-red-200 bg-red-50/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-600" />
            <CardTitle className="text-red-900">Bulk Delete Leads</CardTitle>
          </div>
          <CardDescription className="text-red-700">
            Delete leads based on multiple filter conditions. Companies with no remaining leads will also be deleted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={() => setBulkDeleteDialogOpen(true)} 
            variant="outline" 
            className="border-red-300 text-red-700 hover:bg-red-100"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Open Bulk Delete
          </Button>

          <div className="pt-4 border-t border-red-200">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-red-800">Quick Action: Delete Empty Companies</Label>
            </div>
            <div className="bg-white border border-red-200 rounded-lg p-4 space-y-3">
              <p className="text-sm text-slate-600">
                Delete all companies that have zero leads. This helps clean up your database.
              </p>
              <Button 
                onClick={handleDeleteCompaniesWithNoLeads}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-100"
              >
                <Building2 className="mr-2 h-4 w-4" />
                Delete All Companies with No Leads
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selective Export/Import */}
      <Card className="border-teal-200 bg-teal-50/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-teal-600" />
            <CardTitle className="text-teal-900">Selective Export & Import</CardTitle>
          </div>
          <CardDescription className="text-teal-700">
            Export or import specific collections (tables) individually. Choose exactly which data you want to backup or restore.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <Button 
              onClick={() => setSelectiveExportDialogOpen(true)}
              variant="outline"
              className="flex-1 border-teal-300 text-teal-700 hover:bg-teal-100"
            >
              <Download className="mr-2 h-4 w-4" />
              Export Selected Collections
            </Button>
            <Button 
              onClick={() => setSelectiveImportDialogOpen(true)}
              variant="outline"
              className="flex-1 border-teal-300 text-teal-700 hover:bg-teal-100"
            >
              <Upload className="mr-2 h-4 w-4" />
              Import Selected Collections
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sync Database */}
      <Card className="border-purple-200 bg-purple-50/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-purple-900">Sync Database</CardTitle>
          </div>
          <CardDescription className="text-purple-700">
            Sync data to another MongoDB database. Choose merge mode (add new fields only) or override mode (replace all data).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={() => setSyncDatabaseDialogOpen(true)}
            variant="outline"
            className="border-purple-300 text-purple-700 hover:bg-purple-100"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Open Sync Database
          </Button>
        </CardContent>
      </Card>

      {/* Smart Bulk Delete */}
      <Card className="border-orange-200 bg-orange-50/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-orange-600" />
            <CardTitle className="text-orange-900">Smart Bulk Delete</CardTitle>
          </div>
          <CardDescription className="text-orange-700">
            Automatically delete leads with NO valid contact information (no valid email AND no valid phone number). Companies with no remaining leads will also be deleted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white border border-orange-200 rounded-lg p-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-orange-900 font-semibold">What will be deleted?</Label>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                <li>Leads with NO valid email (null, blank, or invalid format)</li>
                <li>AND NO valid phone number (work phone or mobile phone)</li>
                <li>Companies that have zero leads after deletion</li>
              </ul>
            </div>

            {smartDeletePreview && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">Preview Results:</h4>
                <div className="space-y-1 text-sm text-yellow-800">
                  <p>⚠️ <strong>{smartDeletePreview.leads_count}</strong> leads will be deleted (no valid contact info)</p>
                  <p>⚠️ <strong>{smartDeletePreview.orphaned_companies_count}</strong> companies will be deleted (no remaining leads)</p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button 
                onClick={handleSmartDeletePreview} 
                disabled={loadingSmartPreview}
                variant="outline"
                className="flex-1 border-orange-300 text-orange-700 hover:bg-orange-100"
              >
                {loadingSmartPreview ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Preview Smart Delete
                  </>
                )}
              </Button>

              <Button 
                onClick={handleSmartDelete}
                disabled={executingSmartDelete || !smartDeletePreview || smartDeletePreview.leads_count === 0}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                {executingSmartDelete ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Execute Smart Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Database Backup - Export/Import ALL */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-blue-900">Complete Database Backup & Restore</CardTitle>
          </div>
          <CardDescription className="text-blue-700">
            Export ALL database tables (LOGS + CRM) to a single JSON file for complete backup, or export ENV configuration separately.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Export ALL Data */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FolderDown className="h-5 w-5 text-green-600" />
                <h3 className="font-semibold text-green-800">Export ALL Data</h3>
              </div>
              <p className="text-sm text-green-700 mb-3">
                Download complete database backup as a single JSON file.
              </p>
              <Button 
                variant="outline" 
                className="w-full border-green-500 text-green-700 hover:bg-green-100"
                onClick={handleExportAllData}
                disabled={exportingAll}
              >
                {exportingAll ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Exporting...</>
                ) : (
                  <><Download className="mr-2 h-4 w-4" /> Export ALL</>
                )}
              </Button>
            </div>

            {/* Import ALL Data */}
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FolderUp className="h-5 w-5 text-orange-600" />
                <h3 className="font-semibold text-orange-800">Import ALL Data</h3>
              </div>
              <p className="text-sm text-orange-700 mb-3">
                Restore complete database from a backup JSON file.
              </p>
              <Button 
                variant="outline" 
                className="w-full border-orange-500 text-orange-700 hover:bg-orange-100"
                onClick={() => {
                  setImportAllFile(null);
                  setImportAllResult(null);
                  setImportAllDialogOpen(true);
                }}
              >
                <Upload className="mr-2 h-4 w-4" />
                Import ALL
              </Button>
            </div>

            {/* Export ENV Data */}
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="h-5 w-5 text-purple-600" />
                <h3 className="font-semibold text-purple-800">Export ENV Config</h3>
              </div>
              <p className="text-sm text-purple-700 mb-3">
                Export environment configuration to a separate JSON file.
              </p>
              <Button 
                variant="outline" 
                className="w-full border-purple-500 text-purple-700 hover:bg-purple-100"
                onClick={handleExportEnvData}
                disabled={exportingEnv}
              >
                {exportingEnv ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Exporting...</>
                ) : (
                  <><Download className="mr-2 h-4 w-4" /> Export ENV</>
                )}
              </Button>
            </div>

            {/* Export FORMS */}
            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-5 w-5 text-indigo-600" />
                <h3 className="font-semibold text-indigo-800">Export FORMS</h3>
              </div>
              <p className="text-sm text-indigo-700 mb-3">
                Export all forms with field definitions to a JSON file.
              </p>
              <Button 
                variant="outline" 
                className="w-full border-indigo-500 text-indigo-700 hover:bg-indigo-100"
                onClick={handleExportForms}
                disabled={exportingForms}
              >
                {exportingForms ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Exporting...</>
                ) : (
                  <><Download className="mr-2 h-4 w-4" /> Export FORMS</>
                )}
              </Button>
            </div>

            {/* Import FORMS */}
            <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-5 w-5 text-cyan-600" />
                <h3 className="font-semibold text-cyan-800">Import FORMS</h3>
              </div>
              <p className="text-sm text-cyan-700 mb-3">
                Import forms from an exported forms JSON file.
              </p>
              <Button 
                variant="outline" 
                className="w-full border-cyan-500 text-cyan-700 hover:bg-cyan-100"
                onClick={() => {
                  setImportFormsFile(null);
                  setImportFormsResult(null);
                  setImportFormsDialogOpen(true);
                }}
              >
                <Upload className="mr-2 h-4 w-4" />
                Import FORMS
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Automatic Backup Settings */}
      <Card className="border-indigo-200 bg-indigo-50/30">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-indigo-600" />
              <CardTitle className="text-indigo-900">Automatic Backups</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-indigo-700">
                {backupSettings.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <Switch
                checked={backupSettings.enabled}
                onCheckedChange={(checked) => setBackupSettings({...backupSettings, enabled: checked})}
                data-testid="auto-backup-toggle"
              />
            </div>
          </div>
          <CardDescription className="text-indigo-700">
            Schedule automatic daily backups at 2 AM EST. Backups are stored locally and kept for {backupSettings.retention_days} days.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {backupSettingsLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
            </div>
          ) : (
            <>
              {/* Settings Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-indigo-800">Backup Frequency</Label>
                  <Select 
                    value={backupSettings.frequency} 
                    onValueChange={(val) => setBackupSettings({...backupSettings, frequency: val})}
                  >
                    <SelectTrigger className="mt-1 bg-white" data-testid="backup-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily (2 AM EST)</SelectItem>
                      <SelectItem value="weekly">Weekly (Sunday 2 AM EST)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-indigo-800">Retention Period</Label>
                  <Select 
                    value={String(backupSettings.retention_days)} 
                    onValueChange={(val) => setBackupSettings({...backupSettings, retention_days: parseInt(val)})}
                  >
                    <SelectTrigger className="mt-1 bg-white" data-testid="backup-retention">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="13">13 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button 
                    onClick={handleSaveBackupSettings}
                    disabled={savingBackupSettings}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="save-backup-settings"
                  >
                    {savingBackupSettings ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    Save Settings
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleRunBackupNow}
                    disabled={runningBackup}
                    className="border-indigo-500 text-indigo-700 hover:bg-indigo-100"
                    data-testid="run-backup-now"
                  >
                    {runningBackup ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="mr-2 h-4 w-4" />
                    )}
                    Backup Now
                  </Button>
                </div>
              </div>

              {/* Backup List */}
              {backupList.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-indigo-800 flex items-center gap-2">
                      <Archive className="h-4 w-4" />
                      Stored Backups ({backupList.length})
                    </h4>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={fetchBackupSettings}
                      className="text-indigo-600"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="border rounded-lg bg-white max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-indigo-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-indigo-800">Filename</th>
                          <th className="text-left px-3 py-2 font-medium text-indigo-800">Date</th>
                          <th className="text-right px-3 py-2 font-medium text-indigo-800">Size</th>
                          <th className="text-center px-3 py-2 font-medium text-indigo-800">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backupList.map((backup, idx) => (
                          <tr key={backup.filename} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="px-3 py-2 font-mono text-xs text-slate-700">{backup.filename}</td>
                            <td className="px-3 py-2 text-slate-600">
                              {new Date(backup.created_at).toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-600">
                              {formatFileSize(backup.file_size)}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-indigo-600 hover:text-indigo-800"
                                  onClick={() => handleDownloadBackup(backup.filename)}
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-red-500 hover:text-red-700"
                                  onClick={() => handleDeleteBackup(backup.filename)}
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {backupList.length === 0 && (
                <div className="text-center py-6 text-indigo-600">
                  <Archive className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No backups yet. Click "Backup Now" to create your first backup.</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Environment Variables Card */}
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-emerald-900 flex items-center gap-2">
            <Cable className="h-5 w-5" />
            Environment Variables
          </CardTitle>
          <CardDescription className="text-emerald-600">
            View and edit environment variables for backend and frontend configurations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              <p>Manage API keys, URLs, and other configuration settings stored in .env files.</p>
              <p className="text-amber-600 mt-1 text-xs">⚠️ Changes require service restart to take effect</p>
            </div>
            <Button 
              variant="outline" 
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-100"
              onClick={handleOpenEnvVarsDialog}
            >
              <Settings className="mr-2 h-4 w-4" />
              Manage Variables
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Database Migration Card */}
      <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-purple-900 flex items-center gap-2">
            <Server className="h-5 w-5" />
            Database Migration
          </CardTitle>
          <CardDescription className="text-purple-600">
            Export complete database with schema & indexes, or migrate to a new MongoDB server
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Export Migration Package */}
            <div className="border border-purple-200 rounded-lg p-4 bg-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FolderDown className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium text-purple-900">Export Migration Package</h4>
                  <p className="text-xs text-purple-600">Schema + Indexes + All Data</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                Download a complete migration package containing all collections, indexes, and data. 
                Use this to set up the app on a new server.
              </p>
              <Button
                onClick={() => {
                  setMigrationMode('export');
                  setMigrationStep(2);
                  setMigrationDialogOpen(true);
                }}
                className="w-full bg-purple-600 hover:bg-purple-700"
                data-testid="export-migration-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                Export Migration Package
              </Button>
            </div>

            {/* Import to New Database */}
            <div className="border border-purple-200 rounded-lg p-4 bg-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Globe className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium text-purple-900">Create Database on New Server</h4>
                  <p className="text-xs text-purple-600">Replicate to any MongoDB instance</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                Connect to a new MongoDB server and import your migration package. 
                Creates all collections, indexes, and imports all data.
              </p>
              <Button
                onClick={() => {
                  resetMigrationDialog();
                  setMigrationMode('import');
                  setMigrationStep(2);
                  setMigrationDialogOpen(true);
                }}
                variant="outline"
                className="w-full border-purple-500 text-purple-700 hover:bg-purple-50"
                data-testid="import-migration-btn"
              >
                <Cable className="mr-2 h-4 w-4" />
                Create Database on New Server
              </Button>
            </div>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <p className="text-sm text-purple-800">
              <strong>Use Case:</strong> Migrate your CRM from Emergent's hosted MongoDB to your own cloud server 
              (AWS, Google Cloud, DigitalOcean, etc.) or MongoDB Atlas.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Database Migration Dialog */}
      <Dialog open={migrationDialogOpen} onOpenChange={(open) => {
        setMigrationDialogOpen(open);
        if (!open) resetMigrationDialog();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-900">
              {migrationMode === 'export' ? (
                <>
                  <FolderDown className="h-5 w-5" />
                  Export Migration Package
                </>
              ) : (
                <>
                  <Globe className="h-5 w-5" />
                  Create Database on New Server
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {migrationMode === 'export' 
                ? 'Export complete database with schema, indexes, and all data as JSON.'
                : 'Connect to a new MongoDB server and replicate your database.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Export Mode */}
            {migrationMode === 'export' && (
              <div className="space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-medium text-purple-900 mb-2">Migration Package Contents:</h4>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      All collection schemas and index definitions
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Complete data from all collections
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Users, Leads, Companies, Franchise data, Documents, etc.
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Migration instructions included in file
                    </li>
                  </ul>
                </div>
                
                <Button
                  onClick={handleExportMigration}
                  disabled={exportingMigration}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {exportingMigration ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  {exportingMigration ? 'Exporting...' : 'Download Migration Package'}
                </Button>
              </div>
            )}
            
            {/* Import Mode */}
            {migrationMode === 'import' && (
              <div className="space-y-6">
                {/* Step 1: Upload Migration File */}
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-sm flex items-center justify-center">1</span>
                    Select Migration Package File
                  </h4>
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-4 text-center">
                    <input
                      ref={migrationFileInputRef}
                      type="file"
                      accept=".json"
                      onChange={(e) => {
                        setMigrationFile(e.target.files[0]);
                        setMigrationResult(null);
                      }}
                      className="hidden"
                      id="migration-file-input"
                    />
                    <label htmlFor="migration-file-input" className="cursor-pointer">
                      <FolderUp className="h-10 w-10 text-purple-400 mx-auto mb-2" />
                      <p className="text-sm text-purple-600">
                        {migrationFile ? (
                          <span className="flex items-center justify-center gap-2">
                            <File className="h-4 w-4" />
                            {migrationFile.name}
                          </span>
                        ) : 'Click to select migration package (.json)'}
                      </p>
                    </label>
                  </div>
                </div>
                
                {/* Step 2: Enter Connection Details */}
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-sm flex items-center justify-center">2</span>
                    New MongoDB Connection Details
                  </h4>
                  <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Connection String</Label>
                      <Input
                        type="password"
                        placeholder="mongodb+srv://user:password@cluster.mongodb.net/"
                        value={newDbConnectionString}
                        onChange={(e) => {
                          setNewDbConnectionString(e.target.value);
                          setConnectionTestResult(null);
                        }}
                        className="mt-1 font-mono text-sm"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Get this from MongoDB Atlas or your cloud provider
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Database Name</Label>
                      <Input
                        type="text"
                        placeholder="dailylog_production"
                        value={newDbName}
                        onChange={(e) => {
                          setNewDbName(e.target.value);
                          setConnectionTestResult(null);
                        }}
                        className="mt-1"
                      />
                    </div>
                    <Button
                      onClick={handleTestConnection}
                      disabled={testingConnection || !newDbConnectionString || !newDbName}
                      variant="outline"
                      className="w-full"
                    >
                      {testingConnection ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <TestTube className="mr-2 h-4 w-4" />
                      )}
                      {testingConnection ? 'Testing...' : 'Test Connection'}
                    </Button>
                    
                    {connectionTestResult && (
                      <div className={`p-3 rounded-lg ${connectionTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className="flex items-center gap-2">
                          {connectionTestResult.success ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : (
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                          )}
                          <span className={`font-medium ${connectionTestResult.success ? 'text-green-700' : 'text-red-700'}`}>
                            {connectionTestResult.success ? 'Connection Successful!' : 'Connection Failed'}
                          </span>
                        </div>
                        {connectionTestResult.success && connectionTestResult.server_info && (
                          <p className="text-sm text-green-600 mt-1">
                            MongoDB v{connectionTestResult.server_info.version}
                          </p>
                        )}
                        {connectionTestResult.warning && (
                          <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                            <AlertTriangle className="h-4 w-4" />
                            {connectionTestResult.warning}
                          </p>
                        )}
                        {!connectionTestResult.success && (
                          <p className="text-sm text-red-600 mt-1">{connectionTestResult.message}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Step 3: Migrate */}
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-sm flex items-center justify-center">3</span>
                    Run Migration
                  </h4>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800 flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Warning:</strong> This will DROP all existing collections in the target database 
                        and replace them with data from the migration package. This action cannot be undone.
                      </span>
                    </p>
                  </div>
                  
                  <Button
                    onClick={handleMigrateToNewDatabase}
                    disabled={migratingData || !migrationFile || !connectionTestResult?.success}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {migratingData ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowRight className="mr-2 h-4 w-4" />
                    )}
                    {migratingData ? 'Migrating Data...' : 'Start Migration'}
                  </Button>
                  
                  {migrationResult && (
                    <div className={`p-4 rounded-lg ${migrationResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {migrationResult.success ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-red-600" />
                        )}
                        <span className={`font-medium ${migrationResult.success ? 'text-green-700' : 'text-red-700'}`}>
                          {migrationResult.success ? 'Migration Successful!' : 'Migration Failed'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{migrationResult.message}</p>
                      
                      {migrationResult.results && (
                        <div className="mt-3 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-white rounded p-2">
                              <p className="text-slate-500">Collections Created</p>
                              <p className="font-medium text-slate-800">{migrationResult.results.collections_created?.length || 0}</p>
                            </div>
                            <div className="bg-white rounded p-2">
                              <p className="text-slate-500">Total Documents</p>
                              <p className="font-medium text-slate-800">
                                {Object.values(migrationResult.results.data_imported || {}).reduce((a, b) => a + b, 0)}
                              </p>
                            </div>
                          </div>
                          {migrationResult.results.errors?.length > 0 && (
                            <div className="mt-2 p-2 bg-red-50 rounded">
                              <p className="text-red-700 font-medium">Errors:</p>
                              <ul className="text-red-600 text-xs">
                                {migrationResult.results.errors.map((err, idx) => (
                                  <li key={idx}>{err}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMigrationDialogOpen(false)}>
              {migrationResult?.success ? 'Done' : 'Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import ALL Dialog */}
      <Dialog open={importAllDialogOpen} onOpenChange={(open) => {
        setImportAllDialogOpen(open);
        if (!open) {
          setShowImportWarning(false);
          setImportAllFile(null);
          setImportAllResult(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <FolderUp className="h-5 w-5" />
              Import Complete Database
            </DialogTitle>
            <DialogDescription>
              Select a backup JSON file to restore all database tables.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!showImportWarning ? (
              <>
                <div className="border-2 border-dashed border-orange-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".json"
                    onChange={(e) => {
                      setImportAllFile(e.target.files[0]);
                      setImportAllResult(null);
                    }}
                    className="hidden"
                    id="import-all-file"
                  />
                  <label htmlFor="import-all-file" className="cursor-pointer">
                    <FolderUp className="h-12 w-12 text-orange-400 mx-auto mb-2" />
                    <p className="text-sm text-orange-600">
                      {importAllFile ? importAllFile.name : 'Click to select backup file'}
                    </p>
                  </label>
                </div>
                
                {importAllResult && (
                  <div className={`p-3 rounded-lg ${importAllResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    <p className="font-medium">{importAllResult.success ? 'Import Successful!' : 'Import Failed'}</p>
                    <p className="text-sm">{importAllResult.message}</p>
                    {importAllResult.details && (
                      <div className="mt-2 text-xs">
                        {Object.entries(importAllResult.details).map(([key, val]) => (
                          <div key={key}>{key}: {val.count || 0} records</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                  <p className="text-sm text-amber-700">
                    <strong>Note:</strong> Click "Import Data" to see the warning confirmation before the import begins.
                  </p>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="bg-red-50 border-2 border-red-300 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                    <h3 className="font-bold text-red-800 text-lg">⚠️ WARNING</h3>
                  </div>
                  <p className="text-red-700 font-medium mb-2">
                    This action will PERMANENTLY DELETE all existing data and replace it with the backup file contents.
                  </p>
                  <ul className="text-sm text-red-600 list-disc ml-4 space-y-1">
                    <li>All users, logs, teams, forms will be replaced</li>
                    <li>All CRM data (companies, leads, tasks) will be replaced</li>
                    <li>This action CANNOT be undone</li>
                  </ul>
                </div>
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Selected file:</strong> {importAllFile?.name}
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            {!showImportWarning ? (
              <>
                <Button variant="outline" onClick={() => setImportAllDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => setShowImportWarning(true)}
                  disabled={!importAllFile}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Upload className="mr-2 h-4 w-4" /> Import Data
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowImportWarning(false)}>
                  Go Back
                </Button>
                <Button 
                  onClick={confirmImport}
                  disabled={importingAll}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {importingAll ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Importing...</>
                  ) : (
                    <><AlertTriangle className="mr-2 h-4 w-4" /> Yes, Replace All Data</>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export/Import Results Dialog */}
      <Dialog open={exportResultDialogOpen} onOpenChange={setExportResultDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${exportResultData?.type === 'import' ? 'text-orange-700' : 'text-green-700'}`}>
              {exportResultData?.type === 'import' ? (
                <><FolderUp className="h-5 w-5" /> Import Complete</>
              ) : (
                <><FolderDown className="h-5 w-5" /> Export Complete</>
              )}
            </DialogTitle>
            <DialogDescription>
              {exportResultData?.type === 'import' 
                ? 'Database has been restored from backup file.'
                : 'Database backup has been downloaded.'}
            </DialogDescription>
          </DialogHeader>
          
          {exportResultData && (
            <div className="py-4 space-y-4">
              {/* Summary Stats */}
              <div className={`p-4 rounded-lg ${exportResultData.type === 'import' ? 'bg-orange-50 border border-orange-200' : 'bg-green-50 border border-green-200'}`}>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{exportResultData.totalTables}</p>
                    <p className="text-sm text-slate-600">Tables {exportResultData.type === 'import' ? 'Restored' : 'Exported'}</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{exportResultData.totalRows.toLocaleString()}</p>
                    <p className="text-sm text-slate-600">Total Rows</p>
                  </div>
                </div>
                {exportResultData.filename && (
                  <p className="text-xs text-slate-500 mt-3 text-center truncate">
                    File: {exportResultData.filename}
                  </p>
                )}
              </div>

              {/* Tables List */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  {exportResultData.type === 'import' ? 'Restored' : 'Exported'} Tables ({exportResultData.collections?.length})
                </h4>
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-slate-700">Table Name</th>
                        <th className="text-right px-3 py-2 font-medium text-slate-700">Rows</th>
                        {exportResultData.type === 'import' && (
                          <th className="text-center px-3 py-2 font-medium text-slate-700">Status</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {exportResultData.collections?.map((col, idx) => (
                        <tr key={col.name} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="px-3 py-2 text-slate-800 font-mono text-xs">{col.name}</td>
                          <td className="px-3 py-2 text-right text-slate-600">{col.count.toLocaleString()}</td>
                          {exportResultData.type === 'import' && (
                            <td className="px-3 py-2 text-center">
                              {col.success !== false ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500 inline" />
                              ) : (
                                <AlertTriangle className="h-4 w-4 text-red-500 inline" />
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {exportResultData.createdAt && (
                <p className="text-xs text-slate-500 text-center">
                  Backup created: {new Date(exportResultData.createdAt).toLocaleString()}
                </p>
              )}
              {exportResultData.backupDate && (
                <p className="text-xs text-slate-500 text-center">
                  Original backup date: {new Date(exportResultData.backupDate).toLocaleString()}
                </p>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setExportResultDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <FolderDown className="h-5 w-5" />
              Export Database
            </DialogTitle>
            <DialogDescription>
              Export all data as a full backup or download individual tables.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* One-Click Export Section */}
            <div className="p-4 rounded-lg border-2 border-dashed border-green-300 bg-green-50">
              <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                <Database className="h-4 w-4" />
                One-Click Export (Recommended)
              </h4>
              <p className="text-sm text-green-700 mb-3">
                Export all tables as a single backup file for easy one-click import later.
              </p>
              <Button 
                onClick={handleExportCombined} 
                disabled={exporting}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {exporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Exporting Full Backup...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Export Full Backup
                  </>
                )}
              </Button>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">Or export individual tables</span>
              </div>
            </div>

            <div className="space-y-2">
              {collections.map((col) => {
                const Icon = col.icon;
                return (
                  <div 
                    key={col.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-4 w-4 text-slate-500" />
                      <div>
                        <p className="font-medium text-sm">{col.name}</p>
                        <p className="text-xs text-slate-500">{col.description}</p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleExportTable(col.id)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>

            {/* Export all as separate files button */}
            <Button 
              variant="outline" 
              onClick={handleExportAll} 
              disabled={exporting}
              className="w-full"
            >
              {exporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Export All as Separate Files
                </>
              )}
            </Button>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <FolderUp className="h-5 w-5" />
              Import Database
            </DialogTitle>
            <DialogDescription>
              Import from a full backup file (one-click) or select individual table files.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {!importResult ? (
              <>
                <Alert className="bg-red-50 border-red-200">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 text-sm">
                    <strong>Warning:</strong> Importing will DELETE all existing data in the selected tables 
                    and replace with data from the backup files.
                  </AlertDescription>
                </Alert>

                {/* One-Click Import Section */}
                <div className="p-4 rounded-lg border-2 border-dashed border-green-300 bg-green-50">
                  <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    One-Click Import (Recommended)
                  </h4>
                  <p className="text-sm text-green-700 mb-3">
                    Select a backup folder or file to import all tables at once.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Folder Selection Button */}
                    {'showDirectoryPicker' in window && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleSelectFolder}
                        className="border-green-400 text-green-700 hover:bg-green-100"
                      >
                        <FolderOpen className="mr-2 h-4 w-4" />
                        Select Folder
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => combinedFileInputRef.current?.click()}
                      className="border-green-400 text-green-700 hover:bg-green-100"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Select Backup File
                    </Button>
                    <input
                      type="file"
                      ref={combinedFileInputRef}
                      className="hidden"
                      accept=".json"
                      onChange={handleCombinedFileSelect}
                    />
                    {combinedBackupFile && (
                      <>
                        <span className="text-sm text-green-700 flex items-center gap-1">
                          <File className="h-3 w-3" />
                          {combinedBackupFile.name}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCombinedBackupFile(null);
                            setSelectedFolderName("");
                            setFolderFiles([]);
                            if (combinedFileInputRef.current) combinedFileInputRef.current.value = '';
                          }}
                          className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </div>
                  
                  {/* Show selected folder info */}
                  {selectedFolderName && (
                    <div className="mt-2 p-2 bg-green-100 rounded text-sm">
                      <p className="flex items-center gap-1 text-green-800">
                        <FolderOpen className="h-3 w-3" />
                        <strong>Folder:</strong> {selectedFolderName}
                      </p>
                      {folderFiles.length > 0 && (
                        <p className="text-green-700 text-xs mt-1">
                          Found {folderFiles.length} backup file(s)
                        </p>
                      )}
                    </div>
                  )}
                  
                  {combinedBackupFile && (
                    <Button 
                      onClick={handleImportCombined} 
                      disabled={importing}
                      className="mt-3 w-full bg-green-600 hover:bg-green-700"
                    >
                      {importing ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Importing All Tables...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Import All Tables Now
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-500">Or import individual tables</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {collections.map((col) => {
                    const Icon = col.icon;
                    const selectedFile = selectedImportFiles[col.id];
                    return (
                      <div 
                        key={col.id}
                        className={`p-3 rounded-lg border transition-colors ${
                          selectedFile ? 'bg-orange-50 border-orange-300' : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Icon className="h-4 w-4 text-slate-500" />
                            <div>
                              <p className="font-medium text-sm">{col.name}</p>
                              {selectedFile ? (
                                <p className="text-xs text-orange-600 flex items-center gap-1">
                                  <File className="h-3 w-3" />
                                  {selectedFile.name}
                                </p>
                              ) : (
                                <p className="text-xs text-slate-500">{col.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {selectedFile && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => clearSelectedFile(col.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => fileInputRefs[col.id]?.current?.click()}
                            >
                              <Upload className="h-4 w-4" />
                            </Button>
                            <input
                              type="file"
                              ref={fileInputRefs[col.id]}
                              accept=".json"
                              onChange={(e) => handleFileSelect(col.id, e)}
                              className="hidden"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="text-sm text-slate-500 text-center">
                  {Object.keys(selectedImportFiles).length} of {collections.length} files selected
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <Alert className={Object.values(importResult).every(r => r.success) 
                  ? "bg-green-50 border-green-200" 
                  : "bg-amber-50 border-amber-200"
                }>
                  <CheckCircle2 className={`h-4 w-4 ${
                    Object.values(importResult).every(r => r.success) ? 'text-green-600' : 'text-amber-600'
                  }`} />
                  <AlertDescription>
                    <strong>Import {Object.values(importResult).every(r => r.success) ? 'Completed' : 'Completed with Errors'}</strong>
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-2">
                  {Object.entries(importResult).map(([collection, result]) => (
                    <div 
                      key={collection}
                      className={`p-3 rounded-lg border ${
                        result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm capitalize">{collection.replace('_', ' ')}</span>
                        {result.success ? (
                          <span className="text-sm text-green-700">
                            <CheckCircle2 className="h-4 w-4 inline mr-1" />
                            {result.count} records imported
                          </span>
                        ) : (
                          <span className="text-sm text-red-700">
                            <AlertTriangle className="h-4 w-4 inline mr-1" />
                            {result.error}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              {importResult ? "Close" : "Cancel"}
            </Button>
            {!importResult && (
              <Button 
                variant="destructive"
                onClick={handleImportFiles} 
                disabled={importing || Object.keys(selectedImportFiles).length === 0}
              >
                {importing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Import Selected Files
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Report Dialog */}
      <Dialog open={testReportDialogOpen} onOpenChange={setTestReportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TestTube className="h-5 w-5 text-primary" />
              Generate Test Report
            </DialogTitle>
            <DialogDescription>
              Select which Team Leaders' reports to generate. The report will be downloaded as a PDF (no email will be sent).
              Report is generated for the previous working day.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {loadingTeamLeaders ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : teamLeaders.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No Team Leaders found</p>
                <p className="text-sm">Make sure teams have leaders assigned</p>
              </div>
            ) : (
              <>
                {/* Select All / Deselect All */}
                <div className="flex items-center justify-between mb-4 pb-3 border-b">
                  <span className="text-sm font-medium text-slate-700">
                    {selectedTeamLeaders.length} of {teamLeaders.length} selected
                  </span>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={selectAllTeamLeaders}>
                      Select All
                    </Button>
                    <Button variant="ghost" size="sm" onClick={deselectAllTeamLeaders}>
                      Deselect All
                    </Button>
                  </div>
                </div>
                
                {/* Team Leaders List */}
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                  {teamLeaders.map((leader) => (
                    <div 
                      key={leader.id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
                        selectedTeamLeaders.includes(leader.id) 
                          ? 'bg-primary/5 border-primary/30' 
                          : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                      }`}
                      onClick={() => toggleTeamLeaderSelection(leader.id)}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedTeamLeaders.includes(leader.id)}
                          onCheckedChange={() => toggleTeamLeaderSelection(leader.id)}
                        />
                        <div>
                          <p className="font-medium text-slate-900">{leader.full_name}</p>
                          <p className="text-xs text-slate-500">{leader.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {leader.teams.map((team) => (
                          <div key={team.id} className="flex items-center gap-1 text-xs text-slate-600">
                            <Building2 className="h-3 w-3" />
                            {team.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Send to Team Leaders Checkbox */}
                <div className="mt-4 pt-4 border-t">
                  <div 
                    className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200 cursor-pointer"
                    onClick={() => setSendReportToLeaders(!sendReportToLeaders)}
                  >
                    <Checkbox
                      checked={sendReportToLeaders}
                      onCheckedChange={setSendReportToLeaders}
                    />
                    <div>
                      <p className="font-medium text-blue-900">Send PDF copy to Team Leaders</p>
                      <p className="text-xs text-blue-600">Email the generated report to selected team leaders</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestReportDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateTestReport} 
              disabled={generatingReport || selectedTeamLeaders.length === 0}
            >
              {generatingReport ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate & Download PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CRM Export Dialog */}
      <Dialog open={crmExportDialogOpen} onOpenChange={setCrmExportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700">
              <Briefcase className="h-5 w-5" />
              Export CRM Data
            </DialogTitle>
            <DialogDescription>
              Export all CRM related data as a single JSON backup file.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 rounded-lg border bg-slate-50">
              <h4 className="font-semibold mb-2">This export will include:</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Companies and their details</li>
                <li>• All leads and contact information</li>
                <li>• Lead notes and comments</li>
                <li>• Tasks and reminders</li>
                <li>• Communications history</li>
                <li>• Email/SMS templates</li>
                <li>• Email account connections</li>
                <li>• Social account connections</li>
              </ul>
            </div>
            
            <Button 
              onClick={handleCrmExport} 
              disabled={crmExporting}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              {crmExporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporting CRM Data...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Export All CRM Data
                </>
              )}
            </Button>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCrmExportDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CRM Import Dialog */}
      <Dialog open={crmImportDialogOpen} onOpenChange={setCrmImportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-pink-700">
              <Briefcase className="h-5 w-5" />
              Import CRM Data
            </DialogTitle>
            <DialogDescription>
              Restore all CRM data from a previously exported backup file.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {!crmImportResult ? (
              <>
                <Alert className="bg-red-50 border-red-200">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 text-sm">
                    <strong>Warning:</strong> Importing will DELETE all existing CRM data 
                    and replace with data from the backup file.
                  </AlertDescription>
                </Alert>

                <div className="p-4 rounded-lg border-2 border-dashed border-pink-300 bg-pink-50">
                  <p className="text-sm text-pink-700 mb-3">
                    Select a CRM backup file (crm_full_backup_*.json)
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => crmFileInputRef.current?.click()}
                      className="border-pink-400 text-pink-700 hover:bg-pink-100"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Select Backup File
                    </Button>
                    <input
                      type="file"
                      ref={crmFileInputRef}
                      className="hidden"
                      accept=".json"
                      onChange={(e) => setCrmBackupFile(e.target.files[0])}
                    />
                    {crmBackupFile && (
                      <>
                        <span className="text-sm text-pink-700 flex items-center gap-1">
                          <File className="h-3 w-3" />
                          {crmBackupFile.name}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCrmBackupFile(null);
                            if (crmFileInputRef.current) crmFileInputRef.current.value = '';
                          }}
                          className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </div>
                  
                  {crmBackupFile && (
                    <Button 
                      onClick={handleCrmImport} 
                      disabled={crmImporting}
                      className="mt-3 w-full bg-pink-600 hover:bg-pink-700"
                    >
                      {crmImporting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Importing CRM Data...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Import All CRM Data Now
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <Alert className={Object.values(crmImportResult).every(r => r.success) 
                  ? "bg-green-50 border-green-200" 
                  : "bg-amber-50 border-amber-200"
                }>
                  <CheckCircle2 className={`h-4 w-4 ${
                    Object.values(crmImportResult).every(r => r.success) ? 'text-green-600' : 'text-amber-600'
                  }`} />
                  <AlertDescription>
                    <strong>Import {Object.values(crmImportResult).every(r => r.success) ? 'Completed' : 'Completed with Errors'}</strong>
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {Object.entries(crmImportResult).map(([collection, result]) => (
                    <div 
                      key={collection}
                      className={`p-3 rounded-lg border ${
                        result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm capitalize">{collection.replace(/_/g, ' ')}</span>
                        {result.success ? (
                          <span className="text-sm text-green-700">
                            <CheckCircle2 className="h-4 w-4 inline mr-1" />
                            {result.imported} records
                          </span>
                        ) : (
                          <span className="text-sm text-red-700">
                            <AlertTriangle className="h-4 w-4 inline mr-1" />
                            {result.error}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCrmImportDialogOpen(false)}>
              {crmImportResult ? "Close" : "Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Environment Variables Dialog */}
      <Dialog open={envVarsDialogOpen} onOpenChange={setEnvVarsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cable className="h-5 w-5 text-emerald-600" />
              Environment Variables
            </DialogTitle>
            <DialogDescription>
              View and edit environment variables. Changes require a service restart to take effect.
            </DialogDescription>
          </DialogHeader>

          {loadingEnvVars ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Tabs */}
              <div className="flex border-b mb-4">
                <button
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeEnvTab === 'backend'
                      ? 'text-emerald-600 border-b-2 border-emerald-600'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  onClick={() => setActiveEnvTab('backend')}
                >
                  Backend (.env)
                </button>
                <button
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeEnvTab === 'frontend'
                      ? 'text-emerald-600 border-b-2 border-emerald-600'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  onClick={() => setActiveEnvTab('frontend')}
                >
                  Frontend (.env)
                </button>
              </div>

              {/* Variables List */}
              <div className="flex-1 overflow-auto pr-2 space-y-3">
                {Object.entries(editedEnvVars[activeEnvTab] || {}).map(([key, value]) => (
                  <div key={key} className="flex gap-3 items-start">
                    <div className="w-1/3 min-w-[200px]">
                      <Label className="text-xs text-slate-600 font-mono break-all">{key}</Label>
                    </div>
                    <div className="flex-1 flex gap-2">
                      <Input
                        value={value}
                        onChange={(e) => handleEnvVarChange(activeEnvTab, key, e.target.value)}
                        className="font-mono text-sm"
                        placeholder="(empty)"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="shrink-0"
                        onClick={() => {
                          navigator.clipboard.writeText(value);
                          toast.success("Copied to clipboard");
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                {Object.keys(editedEnvVars[activeEnvTab] || {}).length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No environment variables found
                  </div>
                )}
              </div>

              {/* Save indicator */}
              {hasEnvChanges(activeEnvTab) && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm text-amber-700">
                    You have unsaved changes in {activeEnvTab} environment
                  </span>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4 border-t pt-4">
            <div className="flex justify-between w-full">
              <Button variant="outline" onClick={() => setEnvVarsDialogOpen(false)}>
                Close
              </Button>
              <Button
                onClick={() => handleSaveEnvVars(activeEnvTab)}
                disabled={savingEnvVars || !hasEnvChanges(activeEnvTab)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {savingEnvVars ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save {activeEnvTab === 'backend' ? 'Backend' : 'Frontend'} Variables
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Import FORMS Dialog */}
      <Dialog open={importFormsDialogOpen} onOpenChange={setImportFormsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-cyan-600" />
              Import FORMS
            </DialogTitle>
            <DialogDescription>
              Upload a forms export JSON file to import/update forms
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <Label htmlFor="forms-import-file">Select Forms JSON File</Label>
              <Input
                id="forms-import-file"
                type="file"
                accept=".json"
                onChange={(e) => setImportFormsFile(e.target.files?.[0] || null)}
              />
              {importFormsFile && (
                <div className="bg-cyan-50 border border-cyan-200 p-3 rounded-lg">
                  <p className="text-sm text-cyan-700">
                    <strong>Selected file:</strong> {importFormsFile.name}
                  </p>
                </div>
              )}
            </div>
            
            {importFormsResult && (
              <div className={`p-3 rounded-lg border ${importFormsResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                <p className={`text-sm font-medium ${importFormsResult.error ? 'text-red-800' : 'text-green-800'}`}>
                  {importFormsResult.message || importFormsResult.error}
                </p>
                {importFormsResult.results && (
                  <div className="mt-2 text-xs text-slate-600">
                    <p>Created: {importFormsResult.results.created}</p>
                    <p>Updated: {importFormsResult.results.updated}</p>
                    <p>Skipped: {importFormsResult.results.skipped}</p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportFormsDialogOpen(false)}>
              Close
            </Button>
            <Button 
              onClick={handleImportForms}
              disabled={!importFormsFile || importingForms}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {importingForms ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Importing...</>
              ) : (
                <><Upload className="mr-2 h-4 w-4" /> Import Forms</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Selective Export Dialog */}
      <Dialog open={selectiveExportDialogOpen} onOpenChange={setSelectiveExportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-700">
              <Download className="h-5 w-5" />
              Export Selected Collections
            </DialogTitle>
            <DialogDescription>
              Select the collections you want to export. All data from selected collections will be included.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={selectAllCollections}>
                Select All
              </Button>
              <Button size="sm" variant="outline" onClick={deselectAllCollections}>
                Deselect All
              </Button>
              <Badge variant="outline" className="ml-auto">
                {selectedCollections.length} selected
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {AVAILABLE_COLLECTIONS.map((collection) => (
                <div 
                  key={collection.id}
                  className={`p-3 border rounded-lg cursor-pointer transition-all ${
                    selectedCollections.includes(collection.id)
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-slate-200 hover:border-teal-300'
                  }`}
                  onClick={() => toggleCollection(collection.id)}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedCollections.includes(collection.id)}
                      onCheckedChange={() => toggleCollection(collection.id)}
                    />
                    <collection.icon className="h-4 w-4 text-slate-600" />
                    <span className="font-medium text-sm">{collection.name}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 ml-6">{collection.description}</p>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectiveExportDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExportSelected}
              disabled={exportingSelected || selectedCollections.length === 0}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {exportingSelected ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Export {selectedCollections.length} Collection{selectedCollections.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Selective Import Dialog */}
      <Dialog open={selectiveImportDialogOpen} onOpenChange={setSelectiveImportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-700">
              <Upload className="h-5 w-5" />
              Import Selected Collections
            </DialogTitle>
            <DialogDescription>
              Upload a backup file and import selected collections. This will REPLACE existing data in the selected collections.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                <strong>Warning:</strong> Importing will REPLACE all existing data in the selected collections. This action cannot be undone.
              </p>
            </div>

            <div>
              <Label>Upload Backup File</Label>
              <div className="mt-2">
                <input
                  ref={selectiveImportFileRef}
                  type="file"
                  accept=".json"
                  onChange={(e) => setSelectiveImportFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => selectiveImportFileRef.current?.click()}
                  className="w-full"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {selectiveImportFile ? selectiveImportFile.name : 'Choose JSON File'}
                </Button>
              </div>
            </div>

            {selectiveImportFile && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={selectAllCollections}>
                    Select All
                  </Button>
                  <Button size="sm" variant="outline" onClick={deselectAllCollections}>
                    Deselect All
                  </Button>
                  <Badge variant="outline" className="ml-auto">
                    {selectedCollections.length} selected
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {AVAILABLE_COLLECTIONS.map((collection) => (
                    <div 
                      key={collection.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedCollections.includes(collection.id)
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-slate-200 hover:border-teal-300'
                      }`}
                      onClick={() => toggleCollection(collection.id)}
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedCollections.includes(collection.id)}
                          onCheckedChange={() => toggleCollection(collection.id)}
                        />
                        <collection.icon className="h-4 w-4 text-slate-600" />
                        <span className="font-medium text-sm">{collection.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectiveImportResult && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">Import Results:</h4>
                <div className="space-y-1 text-sm text-green-800">
                  {Object.entries(selectiveImportResult).map(([collection, result]) => (
                    <p key={collection}>
                      {collection}: {result.success ? `✓ ${result.imported || 0} imported` : `✗ ${result.error}`}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setSelectiveImportDialogOpen(false);
              setSelectiveImportFile(null);
              setSelectiveImportResult(null);
              setSelectedCollections([]);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleImportSelected}
              disabled={importingSelected || !selectiveImportFile || selectedCollections.length === 0}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {importingSelected ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Import {selectedCollections.length} Collection{selectedCollections.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Database Dialog */}
      <Dialog open={syncDatabaseDialogOpen} onOpenChange={setSyncDatabaseDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <RefreshCw className="h-5 w-5" />
              Sync Database to Another Server
            </DialogTitle>
            <DialogDescription>
              Sync your current database to another MongoDB server. Choose merge mode (preserve existing data) or override mode (replace all data).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Connection Details */}
            <div className="space-y-3">
              <div>
                <Label>Target MongoDB Connection String *</Label>
                <Input
                  type="password"
                  placeholder="mongodb://username:password@host:port or mongodb+srv://..."
                  value={syncConnectionString}
                  onChange={(e) => setSyncConnectionString(e.target.value)}
                  className="font-mono text-sm"
                />
              </div>
              <div>
                <Label>Target Database Name *</Label>
                <Input
                  placeholder="dailylog_production"
                  value={syncDatabaseName}
                  onChange={(e) => setSyncDatabaseName(e.target.value)}
                />
              </div>

              {/* Test Connection Button */}
              <div className="flex gap-2">
                <Button 
                  onClick={handleTestSyncConnection}
                  disabled={testingSyncConnection || !syncConnectionString || !syncDatabaseName}
                  variant="outline"
                  className="flex-1"
                >
                  {testingSyncConnection ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Cable className="mr-2 h-4 w-4" />
                      Test Connection
                    </>
                  )}
                </Button>
              </div>

              {syncConnectionTestResult && (
                <div className={`p-3 rounded-lg border ${syncConnectionTestResult.success ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                  <p className={`text-sm font-semibold ${syncConnectionTestResult.success ? 'text-green-900' : 'text-red-900'}`}>
                    {syncConnectionTestResult.success ? '✓ Connection Successful' : '✗ Connection Failed'}
                  </p>
                  <p className="text-xs mt-1 text-slate-600">{syncConnectionTestResult.message}</p>
                  {syncConnectionTestResult.database_info && (
                    <div className="mt-2 text-xs text-slate-600">
                      <p>Server: {syncConnectionTestResult.database_info.server_version || 'N/A'}</p>
                      <p>Collections: {syncConnectionTestResult.database_info.collection_count || 0}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sync Mode Selection */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="font-semibold">Sync Mode</Label>
              
              <div className="space-y-2">
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${syncMode === 'merge' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`}
                  onClick={() => setSyncMode('merge')}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={syncMode === 'merge'}
                      onCheckedChange={() => setSyncMode('merge')}
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-sm">Merge Sync (Safe)</p>
                      <p className="text-xs text-slate-600 mt-1">
                        Add new fields and tables without overriding existing data. Existing records are preserved.
                      </p>
                    </div>
                  </div>
                </div>

                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${syncMode === 'override' ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-red-300'}`}
                  onClick={() => setSyncMode('override')}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={syncMode === 'override'}
                      onCheckedChange={() => setSyncMode('override')}
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-red-700">Override Sync (Replace All)</p>
                      <p className="text-xs text-slate-600 mt-1">
                        Replace all data in target database with data from this database. ⚠️ This will DELETE existing data in target.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {syncResult && (
              <div className="bg-green-50 border border-green-300 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">Sync Complete!</h4>
                <div className="space-y-1 text-sm text-green-800">
                  <p>✓ Collections synced: {syncResult.collections_synced}</p>
                  <p>✓ Total records: {syncResult.total_records}</p>
                  <p>✓ Mode: {syncResult.mode === 'merge' ? 'Merge (Safe)' : 'Override (Replace)'}</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setSyncDatabaseDialogOpen(false);
              setSyncConnectionTestResult(null);
              setSyncResult(null);
            }}>
              Close
            </Button>
            <Button 
              onClick={handleSyncDatabase}
              disabled={syncingDatabase || !syncConnectionTestResult?.success}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {syncingDatabase ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Sync Database
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              Bulk Delete Leads
            </DialogTitle>
            <DialogDescription>
              Define conditions to filter and delete leads. All conditions must match (AND logic).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Conditions Builder */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Delete Conditions (ALL must match)</Label>
                <Button size="sm" onClick={addCondition} variant="outline">
                  <Plus className="mr-1 h-3 w-3" /> Add Condition
                </Button>
              </div>

              {deleteConditions.map((condition, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border">
                  <span className="text-xs text-slate-500 w-8">#{index + 1}</span>
                  
                  {/* Field Selector */}
                  <Select
                    value={condition.field}
                    onValueChange={(value) => updateCondition(index, 'field', value)}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_FIELDS_FOR_FILTER.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Operator Selector */}
                  <Select
                    value={condition.operator}
                    onValueChange={(value) => updateCondition(index, 'operator', value)}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OPERATORS.map(op => (
                        <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Value Input (only for non-null operators) */}
                  {!['is_null', 'is_not_null'].includes(condition.operator) && (
                    <Input
                      placeholder="Value..."
                      value={condition.value}
                      onChange={(e) => updateCondition(index, 'value', e.target.value)}
                      className="flex-1"
                    />
                  )}

                  {/* Remove Button */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeCondition(index)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Preview Button */}
            <div className="pt-4 border-t">
              <Button 
                onClick={handlePreviewDelete} 
                disabled={loadingPreview}
                variant="outline"
                className="w-full"
              >
                {loadingPreview ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading Preview...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Preview - Count Matching Leads
                  </>
                )}
              </Button>

              {deletePreviewCount !== null && (
                <div className={`mt-3 p-4 rounded-lg border ${deletePreviewCount > 0 ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300'}`}>
                  <p className={`text-sm font-semibold ${deletePreviewCount > 0 ? 'text-yellow-900' : 'text-green-900'}`}>
                    {deletePreviewCount > 0 
                      ? `⚠️ ${deletePreviewCount} leads will be deleted`
                      : '✓ No leads match these conditions'
                    }
                  </p>
                </div>
              )}
            </div>

            {/* Delete Orphaned Companies Option */}
            <div className="flex items-center space-x-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <Checkbox
                id="delete-orphaned-companies"
                checked={deleteCompaniesWithNoLeads}
                onCheckedChange={setDeleteCompaniesWithNoLeads}
              />
              <Label htmlFor="delete-orphaned-companies" className="text-sm text-orange-900 cursor-pointer">
                Also delete companies that have no leads after this deletion
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkDelete} 
              disabled={deletingLeads || deletePreviewCount === null || deletePreviewCount === 0}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingLeads ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete {deletePreviewCount || 0} Leads
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
